//--------------------------------Attendance_-------------------------->



import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Snackbar,
  Alert,
  LinearProgress,
  CircularProgress,
  Backdrop,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TranslateIcon from "@mui/icons-material/Translate";
import LanguageIcon from "@mui/icons-material/Language";
import { styled } from "@mui/material/styles";
import { useTheme, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDispatch, useSelector } from "react-redux";
import { fetchLocationBased } from "../../actions/LocationBasedAction";
import { fetchLocationBasedByDutyType } from "../../actions/LocationBasedDutyTypeAction";
import { fetchRosterAttendance } from "../../actions/RosterAttendanceAction";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

// ------------ Translation Texts ------------
const translations = {
  en: {
    title: "Employee Strength & Attendance",
    datePicker: "Select Date",
    exportPdf: "Export Full PDF",
    exporting: "Exporting...",
    totalStrength: "Total Strength",
    todayStrength: "Today Strength",
    attendance: "Attendance",
    absent: "Absent",
    location: "Location",
    strength: "Strength",
    attendanceRate: "Attendance Rate (%)",
    chartTitle: "Attendance by Location",
    locationsWithAttendance: "locations with attendance",
    highAttendance: "High Attendance",
    mediumAttendance: "Medium",
    lowAttendance: "Low",
    loading: "Loading attendance data...",
    noData: "No attendance data available for the selected date.",
    total: "Total",
    reportTitle: "Employee Strength & Attendance Report",
    reportDate: "Report Date",
    summaryStats: "Summary Statistics",
    chartViz: "Attendance Visualization by Location",
    detailedData: "Detailed Attendance Data",
    page: "Page",
    of: "of",
    generatedOn: "Generated on",
    pdfSuccess: "PDF exported successfully!",
    pdfError: "Failed to export PDF. Please try again.",
    prepareReport: "Preparing your report with chart and table data...",
    language: "Language",
  },
  si: {
    title: "සේවක පිරිස හා පැමිණීම්",
    datePicker: "දිනය තෝරන්න",
    exportPdf: "PDF බාගත කරන්න",
    exporting: "PDF බාගත කරමින්...",
    totalStrength: "මුළු සේවක පිරිස",
    todayStrength: "අද සේවක පිරිස",
    attendance: "පැමිණි සේවකයන්",
    absent: "පැමිණ නොමැති සේවකයන්",
    location: "ස්ථානය",
    strength: "සේවක පිරිස",
    attendanceRate: "පැමිණීම් අනුපාතය (%)",
    chartTitle: "ස්ථානය අනුව පැමිණීම්",
    locationsWithAttendance: "පැමිණීම් සහිත ස්ථාන",
    highAttendance: "ඉහළ පැමිණීම් අනුපාතය",
    mediumAttendance: "මධ්‍යම පැමිණීම් අනුපාතය",
    lowAttendance: "අඩු පැමිණීම් අනුපාතය",
    loading: "පැමිණීම් දත්ත ලබා ගනිමින්...",
    noData: "තෝරාගත් දිනය සඳහා පැමිණීම් දත්ත නොමැත.",
    total: "සියල්ල",
    reportTitle: "සේවක පිරිස හා පැමිණීම් වාර්තාව",
    reportDate: "වාර්තා දිනය",
    summaryStats: "සාරාංශ සංඛ්‍යාලේඛන",
    chartViz: "ස්ථානය අනුව පැමිණීම් දෘශ්‍යකරණය",
    detailedData: "සවිස්තරාත්මක පැමිණීම් දත්ත",
    page: "පිටුව",
    of: "යි",
    generatedOn: "සෑදූ දිනය",
    pdfSuccess: "PDF ගොනුව සාර්ථකව බාගත කරන ලදී!",
    pdfError: "PDF ගොනුව බාගත කිරීම අසාර්ථක විය. කරුණාකර නැවත උත්සාහ කරන්න.",
    prepareReport: "ප්‍රස්ථාරය සහ වගු දත්ත සමඟ ඔබේ වාර්තාව සකස් කරමින්...",
    language: "භාෂාව",
  }
};

// ------------ Styles ------------
const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "8px 12px",
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("sm")]: {
    padding: "0px 8px",
  },
}));

const StickyCardsContainer = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "0px",
  paddingBottom: "4px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
  [theme.breakpoints.down("sm")]: {
    position: "sticky",
    top: 0,
    backgroundColor: "#ffffff",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "10px",
  boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
  border: "1px solid #d1d9e6",
  backgroundColor: "#ffffff",
  overflow: "hidden",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "2px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
  [theme.breakpoints.up("sm")]: {
    maxHeight: "380px",
    overflowY: "auto",
    overflowX: "auto",
  },
  [theme.breakpoints.down("sm")]: {
    maxHeight: "none",
    overflowY: "visible",
    overflowX: "auto",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "8px 6px",
  fontSize: "13px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.3px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    "&:first-of-type": {
      borderTopLeftRadius: "10px",
    },
    "&:last-of-type": {
      borderTopRightRadius: "10px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "6px 4px",
    fontSize: "11px",
    "&.MuiTableCell-head": {
      fontSize: "12px",
    },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.15s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.06)",
    cursor: "pointer",
  },
  "&:last-of-type td:first-of-type": {
    borderBottomLeftRadius: "10px",
  },
  "&:last-of-type td:last-of-type": {
    borderBottomRightRadius: "10px",
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: "#f9fafc",
  borderRadius: "10px",
  padding: theme.spacing(1.5),
  boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
  border: "1px solid #e6e9ef",
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  color: "#0e3c66",
  marginBottom: theme.spacing(1),
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

const CustomTooltip = ({ active, payload, language }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const t = translations[language];
    
    return (
      <Paper
        sx={{
          padding: "8px 10px",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          border: "1px solid #154c79",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          minWidth: "150px",
        }}
      >
        <Box sx={{ mb: 0.5 }}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 800,
              color: "#154c79",
              textTransform: "uppercase",
            }}
          >
            {data.location}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
              {t.totalStrength}:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>
              {data.total}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
              {t.attendance}:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>
              {data.real} ({data.percentage}%)
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
              {t.absent}:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#F44336" }}>
              {data.absent}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }
  return null;
};

const dutyTypeFilterOptions = [
  "Operational",
  "Traditional",
  "Traditional – Thewa",
  "Temples",
  "Other",
  "External",
];

export default function LocationBasedEmp() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loading, data } = useSelector((state) => state.locationBased);
  const { loading: dutyTypeLoading, data: dutyTypeData } = useSelector((state) => state.locationBasedDutyType);
  const { loading: rosterLoading, data: rosterData } = useSelector((state) => state.rosterAttendance);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [exportProgress, setExportProgress] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [language, setLanguage] = useState('en'); // 'en' or 'si'
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCard, setSelectedCard] = useState('totalStrength');
  const [selectedDutyType, setSelectedDutyType] = useState('Operational'); // '' = All, default = Operational
  
  const chartContainerRef = useRef(null);
  const t = translations[language];

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  useEffect(() => {
    dispatch(fetchLocationBased(formatDate(new Date())));
    dispatch(fetchLocationBasedByDutyType(formatDate(new Date()), 'Operational'));
    dispatch(fetchRosterAttendance(formatDate(new Date())));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    const formatted = formatDate(date);
    dispatch(fetchLocationBased(formatted));
    dispatch(fetchLocationBasedByDutyType(formatted, selectedDutyType || null));
    dispatch(fetchRosterAttendance(formatted));
  };

  const handleDutyTypeChange = (e) => {
    const value = e.target.value; // '' means "All"
    setSelectedDutyType(value);
    dispatch(fetchLocationBasedByDutyType(formatDate(selectedDate), value || null));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLanguageMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    handleLanguageMenuClose();
  };

  // Transform API data
  const transformedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.map((item) => ({
      Emp_location: item.location || item.Emp_location || "",
      Emp_Total: item.TotalEmp || item.Emp_Total || "0",
      Emp_real: item.TodayAtt || item.Emp_real || "0",
      Percentage: item.Percentage || "0",
      location: item.location || item.Emp_location || "",
      TotalEmp: item.TotalEmp || item.Emp_Total || "0",
      TodayAtt: item.TodayAtt || item.Emp_real || "0",
    }));
  }, [data]);

  const sortedData = transformedData.length > 0
    ? [...transformedData].sort((a, b) => Number(b.TodayAtt || 0) - Number(a.TodayAtt || 0))
    : [];

  // Transform the duty-type-filtered API data (same shape as above)
  const transformedDutyTypeData = useMemo(() => {
    if (!dutyTypeData || !Array.isArray(dutyTypeData)) return [];

    return dutyTypeData.map((item) => ({
      location: item.location || item.Emp_location || "",
      TotalEmp: item.TotalEmp || item.Emp_Total || "0",
      TodayAtt: item.TodayAtt || item.Emp_real || "0",
      Percentage: item.Percentage || "0",
    }));
  }, [dutyTypeData]);

  const sortedDutyTypeData = transformedDutyTypeData.length > 0
    ? [...transformedDutyTypeData].sort((a, b) => Number(b.TodayAtt || 0) - Number(a.TodayAtt || 0))
    : [];

  // Prepare chart data based on selected card
  const chartData = useMemo(() => {
    // A specific duty type is selected (not "All") — always use the
    // duty-type-filtered API, regardless of which summary card is active.
    if (selectedDutyType) {
      if (sortedDutyTypeData.length === 0) return [];
      return sortedDutyTypeData
        .filter((item) => Number(item.TodayAtt || 0) > 0)
        .map((item) => ({
          location: item.location,
          total: Number(item.TotalEmp || 0),
          real: Number(item.TodayAtt || 0),
          absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
          percentage: Math.round(Number(item.Percentage || 0)),
        }));
    }

    // For Total Strength card: Use location-based data only
    if (selectedCard === 'totalStrength') {
      if (sortedData.length === 0) return [];
      return sortedData
        .filter((item) => Number(item.TodayAtt || 0) > 0)
        .map((item) => ({
          location: item.location,
          total: Number(item.TotalEmp || 0),
          real: Number(item.TodayAtt || 0),
          absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
          percentage: Math.round(Number(item.Percentage || 0)),
        }));
    }

    // For Today Strength and other cards: Prefer roster API when available, otherwise use location-based data
    if (!rosterLoading && Array.isArray(rosterData) && rosterData.length > 0) {
      return rosterData
        .filter((item) => Number(item.TodayAtt || 0) > 0)
        .map((item) => ({
          location: item.location || item.location,
          total: Number(item.TotalEmp || 0),
          real: Number(item.TodayAtt || 0),
          absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
          percentage: Math.round(Number(item.Percentage || 0)),
        }));
    }

    // Fallback to original location-based data
    if (sortedData.length === 0) return [];

    return sortedData
      .filter((item) => Number(item.TodayAtt || 0) > 0)
      .map((item) => ({
        location: item.location,
        total: Number(item.TotalEmp || 0),
        real: Number(item.TodayAtt || 0),
        absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
        percentage: Math.round(Number(item.Percentage || 0)),
      }));
  }, [sortedData, sortedDutyTypeData, rosterData, rosterLoading, selectedCard, selectedDutyType]);

  const totalStrength =
    !loading && transformedData.length > 0
      ? transformedData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0)
      : 0;

  const activeCount =
    !loading && transformedData.length > 0
      ? transformedData.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0)
      : 0;

  const absentCount = totalStrength - activeCount;

  const activePercentage =
    totalStrength > 0 ? Math.round((activeCount / totalStrength) * 100) : 0;

  // Calculate Today Strength from Roster API
  const todayStrengthCount =
    !rosterLoading && Array.isArray(rosterData) && rosterData.length > 0
      ? rosterData.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0)
      : 0;

  const todayStrengthTotalEmp =
    !rosterLoading && Array.isArray(rosterData) && rosterData.length > 0
      ? rosterData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0)
      : 0;

  const todayStrengthPercentage =
    todayStrengthTotalEmp > 0 ? Math.round((todayStrengthCount / todayStrengthTotalEmp) * 100) : 0;

  // ================= COMBINED PDF EXPORT FUNCTION =================
  const handleExportPDF = async () => {
    if (!chartContainerRef.current || exporting) return;
    
    try {
      setExporting(true);
      setExportProgress(10);
      
      // Step 1: Capture chart as image
      setExportProgress(30);
      const chartCanvas = await html2canvas(chartContainerRef.current, {
        scale: 2,
        backgroundColor: "#F9FAFC",
        logging: false,
        useCORS: true,
      });
      const chartImage = chartCanvas.toDataURL("image/png");
      
      setExportProgress(60);
      
      // Step 2: Create PDF document - ALWAYS IN ENGLISH
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Page 1: Cover page with title and summary - ALWAYS ENGLISH
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("Employee Strength & Attendance Report", pageWidth / 2, 40, { align: "center" });
      
      doc.setFontSize(14);
      doc.setFont("helvetica", "normal");
      doc.text(`Report Date: ${formatDate(selectedDate)}`, pageWidth / 2, 60, { align: "center" });
      
      // Summary statistics - ALWAYS ENGLISH
      doc.setFontSize(12);
      doc.text("Summary Statistics", pageWidth / 2, 80, { align: "center" });
      
      const summaryY = 90;
      doc.text(`Total Strength: ${totalStrength}`, 30, summaryY);
      doc.text(`Total Attendance: ${activeCount} (${activePercentage}%)`, 30, summaryY + 8);
      doc.text(`Total Absent: ${absentCount} (${100 - activePercentage}%)`, 30, summaryY + 16);
      
      // Add decorative line
      doc.setDrawColor(21, 76, 121);
      doc.setLineWidth(0.5);
      doc.line(30, summaryY + 25, pageWidth - 30, summaryY + 25);
      
      setExportProgress(70);
      
      // Page 2: Chart Visualization - ALWAYS ENGLISH
      doc.addPage();
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Attendance Visualization by Location", 20, 20);
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Date: ${formatDate(selectedDate)}`, 20, 28);
      doc.text(`Chart shows ${chartData.length} locations with active attendance`, 20, 34);
      
      // Add chart image
      const chartWidth = pageWidth - 40;
      const chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;
      let chartY = 45;
      
      if (chartHeight > pageHeight - chartY - 20) {
        const maxHeight = pageHeight - chartY - 20;
        const scaledWidth = (chartCanvas.width * maxHeight) / chartCanvas.height;
        const chartX = (pageWidth - scaledWidth) / 2;
        doc.addImage(chartImage, "PNG", chartX, chartY, scaledWidth, maxHeight);
      } else {
        const chartX = (pageWidth - chartWidth) / 2;
        doc.addImage(chartImage, "PNG", chartX, chartY, chartWidth, chartHeight);
      }
      
      setExportProgress(80);
      
      // Page 3: Detailed Table - ALWAYS ENGLISH
      doc.addPage();
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Detailed Attendance Data", 20, 20);
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Date: ${formatDate(selectedDate)}`, 20, 28);
      
      // Prepare table data
      const tableData = sortedData.map((item) => [
        item.location,
        item.TotalEmp.toString(),
        item.TodayAtt.toString(),
        (Number(item.TotalEmp) - Number(item.TodayAtt)).toString(),
        `${Math.round(Number(item.Percentage))}%`,
      ]);
      
      // Add total row - ALWAYS ENGLISH
      tableData.push([
        "TOTAL",
        totalStrength.toString(),
        activeCount.toString(),
        absentCount.toString(),
        `${activePercentage}%`,
      ]);
      
      setExportProgress(90);
      
      // Generate table - ALWAYS ENGLISH
      autoTable(doc, {
        startY: 35,
        head: [["Location", "Total Strength", "Attendance", "Absent", "Attendance Rate (%)"]],
        body: tableData,
        styles: {
          fontSize: 9,
          cellPadding: 4,
        },
        headStyles: {
          fillColor: [21, 76, 121],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 248, 252],
        },
        didDrawCell: (data) => {
          // Highlight total row
          if (data.row.index === tableData.length - 1) {
            doc.setFillColor(21, 76, 121);
            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold");
          }
        },
      });
      
      // Footer on all pages - ALWAYS ENGLISH
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth - 20,
          pageHeight - 10,
          { align: "right" }
        );
        doc.text(
          `Generated on ${new Date().toLocaleDateString()}`,
          20,
          pageHeight - 10
        );
      }

      setExportProgress(100);
      
      // Save the PDF - ALWAYS ENGLISH FILENAME
      const filename = `Complete_Attendance_Report_${formatDate(selectedDate)}.pdf`;
      doc.save(filename);
      
      // Show success message in selected language
      showSnackbar(language === 'si' ? t.pdfSuccess : "PDF exported successfully!", "success");
      
      // Reset progress after a delay
      setTimeout(() => {
        setExporting(false);
        setExportProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      showSnackbar(language === 'si' ? t.pdfError : "Failed to export PDF. Please try again.", "error");
      setExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        {/* Export Progress Backdrop */}
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
          open={exporting}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              p: 4,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              maxWidth: 400,
              width: "100%",
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              sx={{ color: "#154c79" }}
            />
            <Typography variant="h6" color="primary" fontWeight="bold">
              {exporting ? t.exporting : t.exportPdf}
            </Typography>
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={exportProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#2e7d32",
                  },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={1}
              >
                {exportProgress}% Complete
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              {language === 'si' ? t.prepareReport : "Preparing your report with chart and table data..."}
            </Typography>
          </Box>
        </Backdrop>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Header Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#0e3c66",
              textAlign: "center",
              flex: 1,
            }}
          >
            {t.title}
          </Typography>
          
          {/* Language Selector */}
          <Tooltip title={t.language}>
            <IconButton
              onClick={handleLanguageMenuOpen}
              sx={{
                backgroundColor: language === 'si' ? '#142c54' : '#f0f0f0',
                color: language === 'si' ? 'white' : '#142c54',
                '&:hover': {
                  backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
                },
                width: '36px',
                height: '36px',
                ml: 1,
              }}
            >
              {language === 'si' ? (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
              ) : (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
              )}
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 120,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }
            }}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
              sx={{
                backgroundColor: language === 'en' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(20, 44, 84, 0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon sx={{ fontSize: 18, color: '#142c54' }} />
                <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
                  English
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('si')}
              selected={language === 'si'}
              sx={{
                backgroundColor: language === 'si' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(20, 44, 84, 0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TranslateIcon sx={{ fontSize: 18, color: '#142c54' }} />
                <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
                  සිංහල
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Box>

        {/* Date Picker and Export Button - Inline Layout */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1.5,
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<CalendarTodayIcon />}
            onClick={() => setOpenDatePicker(true)}
            disabled={exporting}
            sx={{
              flex: isMobile ? 1 : 3,
              minWidth: isMobile ? "calc(50% - 4px)" : "auto",
              height: "38px",
              borderRadius: "8px",
              backgroundColor: exporting ? "#9e9e9e" : "#142c54",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "13px",
              "&:hover": { 
                backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
              },
            }}
          >
            {formatDate(selectedDate)}
          </Button>

          <FormControl
            size="small"
            disabled={exporting}
            sx={{
              flex: isMobile ? 1 : 3,
              minWidth: isMobile ? "calc(50% - 4px)" : "auto",
              height: "38px",
            }}
          >
            <Select
              value={selectedDutyType}
              onChange={handleDutyTypeChange}
              displayEmpty
              renderValue={(val) => `Duty Type: ${val ? val : "All Duty Types"}`}
              sx={{
                height: "38px",
                borderRadius: "8px",
                backgroundColor: exporting ? "#9e9e9e" : "#142c54",
                fontWeight: "bold",
                fontSize: "13px",
                "& .MuiSelect-select": {
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingRight: "32px !important",
                  height: "38px",
                  boxSizing: "border-box",
                },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-icon": { color: "#fff" },
                "&:hover": {
                  backgroundColor: exporting ? "#9e9e9e" : "#0D47A1",
                },
              }}
            >
              <MenuItem value="">All Duty Types</MenuItem>
              {dutyTypeFilterOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {!loading && transformedData.length > 0 && (
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportPDF}
              disabled={exporting || chartData.length === 0}
              sx={{
                flex: 1,
                minWidth: isMobile ? "calc(50% - 4px)" : "auto",
                height: "38px",
                borderRadius: "8px",
                backgroundColor: exporting 
                  ? "#9e9e9e" 
                  : (chartData.length === 0 ? "#bdbdbd" : "#2e7d32"),
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "13px",
                "&:hover": { 
                  backgroundColor: exporting 
                    ? "#9e9e9e" 
                    : (chartData.length === 0 ? "#bdbdbd" : "#1b5e20") 
                },
              }}
            >
              {exporting ? t.exporting : t.exportPdf}
            </Button>
          )}
        </Box>

        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={handleDateChange}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => (
              <TextField {...params} sx={{ display: "none" }} />
            )}
          />
        )}

        {/* Sticky Cards Section - All in one row */}
        {!loading && transformedData.length > 0 && (
          <StickyCardsContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              {/* Total Strength Card */}
              <Box
                onClick={() => setSelectedCard('totalStrength')}
                sx={{
                  background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
                  border: selectedCard === 'totalStrength' ? "2px solid #0E3C66" : "1px solid #aec4e0",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#0E3C66",
                  flex: 1,
                  minWidth: 0,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                  },
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
                  {t.totalStrength}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {totalStrength}
                </Typography>
              </Box>

              {/* Attendance Card */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
                  border: "1px solid #80cbc4",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#004d40",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
                  {t.attendance}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {activeCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? "9px" : "10px",
                    fontWeight: 700,
                    color:
                      activePercentage > 70
                        ? "#2E7D32"
                        : activePercentage > 50
                        ? "#FF9800"
                        : "#D32F2F",
                  }}
                >
                  ({activePercentage}%)
                </Typography>
              </Box>

              {/* Absent Card */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                  border: "1px solid #ef9a9a",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#b71c1c",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
                  {t.absent}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {absentCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? "9px" : "10px",
                    fontWeight: 700,
                    color: "#D32F2F",
                  }}
                >
                  ({100 - activePercentage}%)
                </Typography>
              </Box>
            </Box>
          </StickyCardsContainer>
        )}

        {/* Content Container */}
        <Box
          sx={{
            [theme.breakpoints.down("sm")]: {
              maxHeight: "calc(100vh - 260px)",
              overflowY: "auto",
            },
          }}
        >
          {/* Chart Section - Added ref for PDF export */}
          {selectedDutyType && dutyTypeLoading && chartData.length === 0 && (
            <ChartContainer>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            </ChartContainer>
          )}
          {chartData.length > 0 && (
            <ChartContainer ref={chartContainerRef}>
              <ChartTitle>
                {selectedDutyType ? `${t.chartTitle} — ${selectedDutyType}` : t.chartTitle} ({chartData.length} {t.locationsWithAttendance})
              </ChartTitle>

              <Box sx={{ width: "100%", height: isMobile ? 450 : 420 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    barSize={isMobile ? 24 : 28}
                    margin={{
                      top: 5,
                      right: isMobile ? 10 : 180,
                      left: 0,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="2 2"
                      stroke="#e0e0e0"
                      horizontal={true}
                      vertical={false}
                    />
                    
                    <YAxis
                      dataKey="location"
                      type="category"
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fontSize: isMobile ? 10 : 11,
                        fontWeight: 500,
                        fill: "#333",
                        textAnchor: "start",
                        dx: -150,
                      }}
                      width={isMobile ? 170 : 180}
                      interval={0}
                      tickFormatter={(value) => value}
                    />

                    <XAxis
                      type="number"
                      tick={{ fontSize: isMobile ? 9 : 10 }}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, "dataMax + 5"]}
                      tickCount={6}
                    />

                    <RechartsTooltip
                      content={<CustomTooltip language={language} />}
                      cursor={{ fill: "rgba(0,0,0,0.03)" }}
                    />

                    <Bar
                      dataKey="real"
                      name="Attendance"
                      fill="#4CAF50"
                      radius={[0, 10, 10, 0]}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`attendance-cell-${index}`}
                          fill={
                            entry.percentage > 70
                              ? "#4CAF50"
                              : entry.percentage > 50
                              ? "#FF9800"
                              : "#F44336"
                          }
                        />
                      ))}

                      <LabelList
                        dataKey="real"
                        position="insideLeft"
                        style={{
                          fontSize: isMobile ? 9 : 10,
                          fontWeight: 700,
                          fill: "#fff",
                        }}
                        offset={12}
                      />

                      <LabelList
                        dataKey="percentage"
                        position="right"
                        formatter={(value) => `${value}%`}
                        style={{
                          fontSize: isMobile ? 10 : 11,
                          fontWeight: 600,
                          fill: "#555",
                        }}
                        offset={8}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* Color Legend */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "15px",
                  mt: 1,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box
                    sx={{
                      width: "14px",
                      height: "14px",
                      backgroundColor: "#4CAF50",
                      borderRadius: "3px",
                    }}
                  />
                  <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                    {t.highAttendance} ({'>'}70%)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box
                    sx={{
                      width: "14px",
                      height: "14px",
                      backgroundColor: "#FF9800",
                      borderRadius: "3px",
                    }}
                  />
                  <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                    {t.mediumAttendance} (50-70%)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box
                    sx={{
                      width: "14px",
                      height: "14px",
                      backgroundColor: "#F44336",
                      borderRadius: "3px",
                    }}
                  />
                  <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                    {t.lowAttendance} ({'<'}50%)
                  </Typography>
                </Box>
              </Box>
            </ChartContainer>
          )}

          {/* TABLE SECTION */}
          <StyledTableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell>{t.location}</StyledTableCell>
                  <StyledTableCell>{t.strength}</StyledTableCell>
                  <StyledTableCell>{t.attendance}</StyledTableCell>
                  <StyledTableCell>{t.attendanceRate}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && transformedData.length > 0 ? (
                  <>
                    {sortedData.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell
                          sx={{
                            textAlign: "left",
                            pl: isMobile ? 2 : 3,
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.location}
                        </StyledTableCell>
                        <StyledTableCell>{row.TotalEmp}</StyledTableCell>
                        <StyledTableCell>{row.TodayAtt}</StyledTableCell>
                        <StyledTableCell>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>{Math.round(Number(row.Percentage))}%</span>
                            <Box
                              sx={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor:
                                  Number(row.Percentage) > 70
                                    ? "#4CAF50"
                                    : Number(row.Percentage) > 50
                                    ? "#FF9800"
                                    : "#F44336",
                              }}
                            />
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                    <StyledTableRow
                      sx={{
                        backgroundColor: "#154c79 !important",
                        "&:hover": { backgroundColor: "#154c79 !important" },
                      }}
                    >
                      <StyledTableCell
                        sx={{
                          color: "#ffffff",
                          fontWeight: 700,
                          textAlign: "left",
                          pl: isMobile ? 2 : 3,
                        }}
                      >
                        {t.total}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        {totalStrength}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        {activeCount}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span>{activePercentage}%</span>
                          <Box
                            sx={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor:
                                activePercentage > 70
                                  ? "#4CAF50"
                                  : activePercentage > 50
                                  ? "#FF9800"
                                  : "#F44336",
                            }}
                          />
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  </>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                      <Typography
                        sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
                      >
                        {t.loading}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                      <Typography
                        sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
                      >
                        {t.noData}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Box>
      </PageContainer>
    </LocalizationProvider>
  );
}






// import React, { useEffect, useState, useMemo, useRef } from "react";
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   Paper,
//   Table,
//   TableHead,
//   TableBody,
//   TableRow,
//   TableCell,
//   TableContainer,
//   Snackbar,
//   Alert,
//   LinearProgress,
//   CircularProgress,
//   Backdrop,
//   IconButton,
//   Menu,
//   MenuItem,
//   Tooltip,
// } from "@mui/material";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import TranslateIcon from "@mui/icons-material/Translate";
// import LanguageIcon from "@mui/icons-material/Language";
// import { styled } from "@mui/material/styles";
// import { useTheme, useMediaQuery } from "@mui/material";
// import { LocalizationProvider } from "@mui/x-date-pickers";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchLocationBased } from "../../actions/LocationBasedAction";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip as RechartsTooltip,
//   ResponsiveContainer,
//   CartesianGrid,
//   Cell,
//   LabelList,
// } from "recharts";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import html2canvas from "html2canvas";

// // ------------ Translation Texts ------------
// const translations = {
//   en: {
//     title: "Employee Strength & Attendance",
//     datePicker: "Select Date",
//     exportPdf: "Export Full PDF",
//     exporting: "Exporting...",
//     totalStrength: "Total Strength",
//     attendance: "Attendance",
//     absent: "Absent",
//     location: "Location",
//     strength: "Strength",
//     attendanceRate: "Attendance Rate (%)",
//     chartTitle: "Attendance by Location",
//     locationsWithAttendance: "locations with attendance",
//     highAttendance: "High Attendance",
//     mediumAttendance: "Medium",
//     lowAttendance: "Low",
//     loading: "Loading attendance data...",
//     noData: "No attendance data available for the selected date.",
//     total: "Total",
//     reportTitle: "Employee Strength & Attendance Report",
//     reportDate: "Report Date",
//     summaryStats: "Summary Statistics",
//     chartViz: "Attendance Visualization by Location",
//     detailedData: "Detailed Attendance Data",
//     page: "Page",
//     of: "of",
//     generatedOn: "Generated on",
//     pdfSuccess: "PDF exported successfully!",
//     pdfError: "Failed to export PDF. Please try again.",
//     prepareReport: "Preparing your report with chart and table data...",
//     language: "Language",
//   },
//   si: {
//     title: "සේවක පිරිස හා පැමිණීම්",
//     datePicker: "දිනය තෝරන්න",
//     exportPdf: "PDF බාගත කරන්න",
//     exporting: "PDF බාගත කරමින්...",
//     totalStrength: "මුළු සේවක පිරිස",
//     attendance: "පැමිණි සේවකයන්",
//     absent: "පැමිණ නොමැති සේවකයන්",
//     location: "ස්ථානය",
//     strength: "සේවක පිරිස",
//     attendanceRate: "පැමිණීම් අනුපාතය (%)",
//     chartTitle: "ස්ථානය අනුව පැමිණීම්",
//     locationsWithAttendance: "පැමිණීම් සහිත ස්ථාන",
//     highAttendance: "ඉහළ පැමිණීම් අනුපාතය",
//     mediumAttendance: "මධ්‍යම පැමිණීම් අනුපාතය",
//     lowAttendance: "අඩු පැමිණීම් අනුපාතය",
//     loading: "පැමිණීම් දත්ත ලබා ගනිමින්...",
//     noData: "තෝරාගත් දිනය සඳහා පැමිණීම් දත්ත නොමැත.",
//     total: "සියල්ල",
//     reportTitle: "සේවක පිරිස හා පැමිණීම් වාර්තාව",
//     reportDate: "වාර්තා දිනය",
//     summaryStats: "සාරාංශ සංඛ්‍යාලේඛන",
//     chartViz: "ස්ථානය අනුව පැමිණීම් දෘශ්‍යකරණය",
//     detailedData: "සවිස්තරාත්මක පැමිණීම් දත්ත",
//     page: "පිටුව",
//     of: "යි",
//     generatedOn: "සෑදූ දිනය",
//     pdfSuccess: "PDF ගොනුව සාර්ථකව බාගත කරන ලදී!",
//     pdfError: "PDF ගොනුව බාගත කිරීම අසාර්ථක විය. කරුණාකර නැවත උත්සාහ කරන්න.",
//     prepareReport: "ප්‍රස්ථාරය සහ වගු දත්ත සමඟ ඔබේ වාර්තාව සකස් කරමින්...",
//     language: "භාෂාව",
//   }
// };

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "380px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "none",
//     overflowY: "visible",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const ChartContainer = styled(Box)(({ theme }) => ({
//   position: "relative",
//   backgroundColor: "#f9fafc",
//   borderRadius: "10px",
//   padding: theme.spacing(1.5),
//   boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
//   border: "1px solid #e6e9ef",
//   marginBottom: theme.spacing(1.5),
//   [theme.breakpoints.down("sm")]: {
//     padding: theme.spacing(1),
//   },
// }));

// const ChartTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "14px",
//   fontWeight: 700,
//   color: "#0e3c66",
//   marginBottom: theme.spacing(1),
//   textAlign: "center",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "12px",
//   },
// }));

// const CustomTooltip = ({ active, payload, language }) => {
//   if (active && payload && payload.length) {
//     const data = payload[0].payload;
//     const t = translations[language];
    
//     return (
//       <Paper
//         sx={{
//           padding: "8px 10px",
//           backgroundColor: "rgba(255, 255, 255, 0.98)",
//           border: "1px solid #154c79",
//           borderRadius: "6px",
//           boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
//           minWidth: "150px",
//         }}
//       >
//         <Box sx={{ mb: 0.5 }}>
//           <Typography
//             sx={{
//               fontSize: "12px",
//               fontWeight: 800,
//               color: "#154c79",
//               textTransform: "uppercase",
//             }}
//           >
//             {data.location}
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.totalStrength}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>
//               {data.total}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.attendance}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>
//               {data.real} ({data.percentage}%)
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               {t.absent}:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#F44336" }}>
//               {data.absent}
//             </Typography>
//           </Box>
//         </Box>
//       </Paper>
//     );
//   }
//   return null;
// };

// export default function LocationBasedEmp() {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { loading, data } = useSelector((state) => state.locationBased);

//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [exportProgress, setExportProgress] = useState(0);
//   const [exporting, setExporting] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });
//   const [language, setLanguage] = useState('en'); // 'en' or 'si'
//   const [anchorEl, setAnchorEl] = useState(null);
  
//   const chartContainerRef = useRef(null);
//   const t = translations[language];

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchLocationBased(formatDate(new Date())));
//   }, [dispatch]);

//   const handleDateChange = (date) => {
//     setSelectedDate(date);
//     setOpenDatePicker(false);
//     const formatted = formatDate(date);
//     dispatch(fetchLocationBased(formatted));
//   };

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({
//       open: true,
//       message,
//       severity,
//     });
//   };

//   const closeSnackbar = () => {
//     setSnackbar({ ...snackbar, open: false });
//   };

//   const handleLanguageMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleLanguageMenuClose = () => {
//     setAnchorEl(null);
//   };

//   const handleLanguageChange = (lang) => {
//     setLanguage(lang);
//     handleLanguageMenuClose();
//   };

//   // Transform API data
//   const transformedData = useMemo(() => {
//     if (!data || !Array.isArray(data)) return [];

//     return data.map((item) => ({
//       Emp_location: item.location || item.Emp_location || "",
//       Emp_Total: item.TotalEmp || item.Emp_Total || "0",
//       Emp_real: item.TodayAtt || item.Emp_real || "0",
//       Percentage: item.Percentage || "0",
//       location: item.location || item.Emp_location || "",
//       TotalEmp: item.TotalEmp || item.Emp_Total || "0",
//       TodayAtt: item.TodayAtt || item.Emp_real || "0",
//     }));
//   }, [data]);

//   const sortedData = transformedData.length > 0
//     ? [...transformedData].sort((a, b) => Number(b.TodayAtt || 0) - Number(a.TodayAtt || 0))
//     : [];

//   // Prepare chart data - Filter out locations with 0% attendance and sort by attendance
//   const chartData = useMemo(() => {
//     if (sortedData.length === 0) return [];

//     return sortedData
//       .filter((item) => Number(item.TodayAtt || 0) > 0)
//       .map((item) => ({
//         location: item.location,
//         total: Number(item.TotalEmp || 0),
//         real: Number(item.TodayAtt || 0),
//         absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//         percentage: Math.round(Number(item.Percentage || 0)),
//       }));
//   }, [sortedData]);

//   const totalStrength =
//     !loading && transformedData.length > 0
//       ? transformedData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0)
//       : 0;

//   const activeCount =
//     !loading && transformedData.length > 0
//       ? transformedData.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0)
//       : 0;

//   const absentCount = totalStrength - activeCount;

//   const activePercentage =
//     totalStrength > 0 ? Math.round((activeCount / totalStrength) * 100) : 0;

//   // ================= COMBINED PDF EXPORT FUNCTION =================
//   const handleExportPDF = async () => {
//     if (!chartContainerRef.current || exporting) return;
    
//     try {
//       setExporting(true);
//       setExportProgress(10);
      
//       // Step 1: Capture chart as image
//       setExportProgress(30);
//       const chartCanvas = await html2canvas(chartContainerRef.current, {
//         scale: 2,
//         backgroundColor: "#F9FAFC",
//         logging: false,
//         useCORS: true,
//       });
//       const chartImage = chartCanvas.toDataURL("image/png");
      
//       setExportProgress(60);
      
//       // Step 2: Create PDF document
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();
      
//       // Page 1: Cover page with title and summary
//       doc.setFontSize(20);
//       doc.setFont("helvetica", "bold");
//       doc.text(language === 'si' ? t.reportTitle : "Employee Strength & Attendance Report", pageWidth / 2, 40, { align: "center" });
      
//       doc.setFontSize(14);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${language === 'si' ? t.reportDate : "Report Date"}: ${formatDate(selectedDate)}`, pageWidth / 2, 60, { align: "center" });
      
//       // Summary statistics
//       doc.setFontSize(12);
//       doc.text(language === 'si' ? t.summaryStats : "Summary Statistics", pageWidth / 2, 80, { align: "center" });
      
//       const summaryY = 90;
//       doc.text(`${language === 'si' ? t.totalStrength : "Total Strength"}: ${totalStrength}`, 30, summaryY);
//       doc.text(`${language === 'si' ? t.attendance : "Total Attendance"}: ${activeCount} (${activePercentage}%)`, 30, summaryY + 8);
//       doc.text(`${language === 'si' ? t.absent : "Total Absent"}: ${absentCount} (${100 - activePercentage}%)`, 30, summaryY + 16);
      
//       // Add decorative line
//       doc.setDrawColor(21, 76, 121);
//       doc.setLineWidth(0.5);
//       doc.line(30, summaryY + 25, pageWidth - 30, summaryY + 25);
      
//       setExportProgress(70);
      
//       // Page 2: Chart Visualization
//       doc.addPage();
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text(language === 'si' ? t.chartViz : "Attendance Visualization by Location", 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${language === 'si' ? t.reportDate : "Date"}: ${formatDate(selectedDate)}`, 20, 28);
//       doc.text(`${language === 'si' ? `ප්‍රස්ථාරය ${chartData.length} ${t.locationsWithAttendance} පෙන්වයි` : `Chart shows ${chartData.length} locations with active attendance`}`, 20, 34);
      
//       // Add chart image
//       const chartWidth = pageWidth - 40;
//       const chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;
//       let chartY = 45;
      
//       if (chartHeight > pageHeight - chartY - 20) {
//         const maxHeight = pageHeight - chartY - 20;
//         const scaledWidth = (chartCanvas.width * maxHeight) / chartCanvas.height;
//         const chartX = (pageWidth - scaledWidth) / 2;
//         doc.addImage(chartImage, "PNG", chartX, chartY, scaledWidth, maxHeight);
//       } else {
//         const chartX = (pageWidth - chartWidth) / 2;
//         doc.addImage(chartImage, "PNG", chartX, chartY, chartWidth, chartHeight);
//       }
      
//       setExportProgress(80);
      
//       // Page 3: Detailed Table
//       doc.addPage();
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text(language === 'si' ? t.detailedData : "Detailed Attendance Data", 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${language === 'si' ? t.reportDate : "Date"}: ${formatDate(selectedDate)}`, 20, 28);
      
//       // Prepare table data
//       const tableData = sortedData.map((item) => [
//         item.location,
//         item.TotalEmp.toString(),
//         item.TodayAtt.toString(),
//         (Number(item.TotalEmp) - Number(item.TodayAtt)).toString(),
//         `${Math.round(Number(item.Percentage))}%`,
//       ]);
      
//       // Add total row
//       tableData.push([
//         language === 'si' ? t.total : "TOTAL",
//         totalStrength.toString(),
//         activeCount.toString(),
//         absentCount.toString(),
//         `${activePercentage}%`,
//       ]);
      
//       setExportProgress(90);
      
//       // Generate table
//       autoTable(doc, {
//         startY: 35,
//         head: [
//           language === 'si' 
//             ? [t.location, t.totalStrength, t.attendance, t.absent, t.attendanceRate]
//             : ["Location", "Total Strength", "Attendance", "Absent", "Attendance Rate (%)"]
//         ],
//         body: tableData,
//         styles: {
//           fontSize: 9,
//           cellPadding: 4,
//         },
//         headStyles: {
//           fillColor: [21, 76, 121],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         alternateRowStyles: {
//           fillColor: [245, 248, 252],
//         },
//         didDrawCell: (data) => {
//           // Highlight total row
//           if (data.row.index === tableData.length - 1) {
//             doc.setFillColor(21, 76, 121);
//             doc.setTextColor(255, 255, 255);
//             doc.setFont("helvetica", "bold");
//           }
//         },
//       });
      
//       // Footer on all pages
//       const pageCount = doc.internal.getNumberOfPages();
//       for (let i = 1; i <= pageCount; i++) {
//         doc.setPage(i);
//         doc.setFontSize(10);
//         doc.setTextColor(128, 128, 128);
//         doc.text(
//           `${language === 'si' ? t.page : "Page"} ${i} ${language === 'si' ? t.of : "of"} ${pageCount}`,
//           pageWidth - 20,
//           pageHeight - 10,
//           { align: "right" }
//         );
//         doc.text(
//           `${language === 'si' ? t.generatedOn : "Generated on"} ${new Date().toLocaleDateString()}`,
//           20,
//           pageHeight - 10
//         );
//       }
      
//       setExportProgress(100);
      
//       // Save the PDF
//       const filename = language === 'si' 
//         ? `සේවක_පිරිස_පැමිණීම්_වාර්තා_${formatDate(selectedDate)}.pdf`
//         : `Complete_Attendance_Report_${formatDate(selectedDate)}.pdf`;
//       doc.save(filename);
      
//       // Show success message
//       showSnackbar(language === 'si' ? t.pdfSuccess : "PDF exported successfully!", "success");
      
//       // Reset progress after a delay
//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
      
//     } catch (error) {
//       console.error("Error generating PDF:", error);
//       showSnackbar(language === 'si' ? t.pdfError : "Failed to export PDF. Please try again.", "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               {exporting ? t.exporting : t.exportPdf}
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   backgroundColor: "#e0e0e0",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: "#2e7d32",
//                   },
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 color="text.secondary"
//                 align="center"
//                 mt={1}
//               >
//                 {exportProgress}% Complete
//               </Typography>
//             </Box>
//             <Typography variant="body2" color="text.secondary" align="center">
//               {language === 'si' ? t.prepareReport : "Preparing your report with chart and table data..."}
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Success/Error Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={closeSnackbar}
//           anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         >
//           <Alert
//             onClose={closeSnackbar}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//             elevation={6}
//             variant="filled"
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>

//         {/* Header Section */}
//         <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
//           <Typography
//             sx={{
//               fontSize: "16px",
//               fontWeight: 700,
//               color: "#0e3c66",
//               textAlign: "center",
//               flex: 1,
//             }}
//           >
//             {t.title}
//           </Typography>
          
//           {/* Language Selector */}
//           <Tooltip title={t.language}>
//             <IconButton
//               onClick={handleLanguageMenuOpen}
//               sx={{
//                 backgroundColor: language === 'si' ? '#142c54' : '#f0f0f0',
//                 color: language === 'si' ? 'white' : '#142c54',
//                 '&:hover': {
//                   backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
//                 },
//                 width: '36px',
//                 height: '36px',
//                 ml: 1,
//               }}
//             >
//               {language === 'si' ? (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
//               ) : (
//                 <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
//               )}
//             </IconButton>
//           </Tooltip>
          
//           <Menu
//             anchorEl={anchorEl}
//             open={Boolean(anchorEl)}
//             onClose={handleLanguageMenuClose}
//             PaperProps={{
//               sx: {
//                 mt: 1,
//                 minWidth: 120,
//                 borderRadius: '8px',
//                 boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
//               }
//             }}
//           >
//             <MenuItem 
//               onClick={() => handleLanguageChange('en')}
//               selected={language === 'en'}
//               sx={{
//                 backgroundColor: language === 'en' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <LanguageIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
//                   English
//                 </Typography>
//               </Box>
//             </MenuItem>
//             <MenuItem 
//               onClick={() => handleLanguageChange('si')}
//               selected={language === 'si'}
//               sx={{
//                 backgroundColor: language === 'si' ? 'rgba(20, 44, 84, 0.08)' : 'transparent',
//                 '&:hover': {
//                   backgroundColor: 'rgba(20, 44, 84, 0.12)',
//                 },
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <TranslateIcon sx={{ fontSize: 18, color: '#142c54' }} />
//                 <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
//                   සිංහල
//                 </Typography>
//               </Box>
//             </MenuItem>
//           </Menu>
//         </Box>

//         {/* Date Picker and Export Button - Inline Layout */}
//         <Box
//           sx={{
//             display: "flex",
//             gap: 1,
//             mb: 1.5,
//             alignItems: "center",
//             flexWrap: isMobile ? "wrap" : "nowrap",
//           }}
//         >
//           <Button
//             variant="contained"
//             startIcon={<CalendarTodayIcon />}
//             onClick={() => setOpenDatePicker(true)}
//             disabled={exporting}
//             sx={{
//               flex: isMobile ? 1 : 3,
//               minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//               height: "38px",
//               borderRadius: "8px",
//               backgroundColor: exporting ? "#9e9e9e" : "#142c54",
//               color: "#fff",
//               fontWeight: "bold",
//               textTransform: "none",
//               fontSize: "13px",
//               "&:hover": { 
//                 backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//               },
//             }}
//           >
//             {formatDate(selectedDate)}
//           </Button>

//           {!loading && transformedData.length > 0 && (
//             <Button
//               variant="contained"
//               startIcon={<PictureAsPdfIcon />}
//               onClick={handleExportPDF}
//               disabled={exporting || chartData.length === 0}
//               sx={{
//                 flex: 1,
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//                 height: "38px",
//                 borderRadius: "8px",
//                 backgroundColor: exporting 
//                   ? "#9e9e9e" 
//                   : (chartData.length === 0 ? "#bdbdbd" : "#2e7d32"),
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "13px",
//                 "&:hover": { 
//                   backgroundColor: exporting 
//                     ? "#9e9e9e" 
//                     : (chartData.length === 0 ? "#bdbdbd" : "#1b5e20") 
//                 },
//               }}
//             >
//               {exporting ? t.exporting : t.exportPdf}
//             </Button>
//           )}
//         </Box>

//         {openDatePicker && (
//           <DatePicker
//             open={openDatePicker}
//             value={selectedDate}
//             onChange={handleDateChange}
//             onClose={() => setOpenDatePicker(false)}
//             renderInput={(params) => (
//               <TextField {...params} sx={{ display: "none" }} />
//             )}
//           />
//         )}

//         {/* Sticky Cards Section - All in one row */}
//         {!loading && transformedData.length > 0 && (
//           <StickyCardsContainer>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "6px",
//               }}
//             >
//               {/* Total Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "1px solid #aec4e0",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.totalStrength}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStrength}
//                 </Typography>
//               </Box>

//               {/* Attendance Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                   border: "1px solid #80cbc4",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#004d40",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.attendance}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {activeCount}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color:
//                       activePercentage > 70
//                         ? "#2E7D32"
//                         : activePercentage > 50
//                         ? "#FF9800"
//                         : "#D32F2F",
//                   }}
//                 >
//                   ({activePercentage}%)
//                 </Typography>
//               </Box>

//               {/* Absent Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
//                   border: "1px solid #ef9a9a",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#b71c1c",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   {t.absent}
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {absentCount}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color: "#D32F2F",
//                   }}
//                 >
//                   ({100 - activePercentage}%)
//                 </Typography>
//               </Box>
//             </Box>
//           </StickyCardsContainer>
//         )}

//         {/* Content Container */}
//         <Box
//           sx={{
//             [theme.breakpoints.down("sm")]: {
//               maxHeight: "calc(100vh - 260px)",
//               overflowY: "auto",
//             },
//           }}
//         >
//           {/* Chart Section - Added ref for PDF export */}
//           {chartData.length > 0 && (
//             <ChartContainer ref={chartContainerRef}>
//               <ChartTitle>
//                 {t.chartTitle} ({chartData.length} {t.locationsWithAttendance})
//               </ChartTitle>

//               <Box sx={{ width: "100%", height: isMobile ? 450 : 420 }}>
//                 <ResponsiveContainer width="100%" height="100%">
//                   <BarChart
//                     data={chartData}
//                     layout="vertical"
//                     barSize={isMobile ? 24 : 28}
//                     margin={{
//                       top: 5,
//                       right: isMobile ? 10 : 180,
//                       left: 0,
//                       bottom: 5,
//                     }}
//                   >
//                     <CartesianGrid
//                       strokeDasharray="2 2"
//                       stroke="#e0e0e0"
//                       horizontal={true}
//                       vertical={false}
//                     />
                    
//                     <YAxis
//                       dataKey="location"
//                       type="category"
//                       axisLine={false}
//                       tickLine={false}
//                       tick={{
//                         fontSize: isMobile ? 10 : 11,
//                         fontWeight: 500,
//                         fill: "#333",
//                         textAnchor: "start",
//                         dx: -150,
//                       }}
//                       width={isMobile ? 170 : 180}
//                       interval={0}
//                       tickFormatter={(value) => value}
//                     />

//                     <XAxis
//                       type="number"
//                       tick={{ fontSize: isMobile ? 9 : 10 }}
//                       axisLine={false}
//                       tickLine={false}
//                       domain={[0, "dataMax + 5"]}
//                       tickCount={6}
//                     />

//                     <RechartsTooltip
//                       content={<CustomTooltip language={language} />}
//                       cursor={{ fill: "rgba(0,0,0,0.03)" }}
//                     />

//                     <Bar
//                       dataKey="real"
//                       name="Attendance"
//                       fill="#4CAF50"
//                       radius={[0, 10, 10, 0]}
//                     >
//                       {chartData.map((entry, index) => (
//                         <Cell
//                           key={`attendance-cell-${index}`}
//                           fill={
//                             entry.percentage > 70
//                               ? "#4CAF50"
//                               : entry.percentage > 50
//                               ? "#FF9800"
//                               : "#F44336"
//                           }
//                         />
//                       ))}

//                       <LabelList
//                         dataKey="real"
//                         position="insideLeft"
//                         style={{
//                           fontSize: isMobile ? 9 : 10,
//                           fontWeight: 700,
//                           fill: "#fff",
//                         }}
//                         offset={12}
//                       />

//                       <LabelList
//                         dataKey="percentage"
//                         position="right"
//                         formatter={(value) => `${value}%`}
//                         style={{
//                           fontSize: isMobile ? 10 : 11,
//                           fontWeight: 600,
//                           fill: "#555",
//                         }}
//                         offset={8}
//                       />
//                     </Bar>
//                   </BarChart>
//                 </ResponsiveContainer>
//               </Box>

//               {/* Color Legend */}
//               <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   gap: "15px",
//                   mt: 1,
//                   flexWrap: "wrap",
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#4CAF50",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.highAttendance} ({'>'}70%)
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#FF9800",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.mediumAttendance} (50-70%)
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#F44336",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     {t.lowAttendance} ({'<'}50%)
//                   </Typography>
//                 </Box>
//               </Box>
//             </ChartContainer>
//           )}

//           {/* TABLE SECTION */}
//           <StyledTableContainer component={Paper}>
//             <Table stickyHeader>
//               <TableHead>
//                 <TableRow>
//                   <StyledTableCell>{t.location}</StyledTableCell>
//                   <StyledTableCell>{t.strength}</StyledTableCell>
//                   <StyledTableCell>{t.attendance}</StyledTableCell>
//                   <StyledTableCell>{t.attendanceRate}</StyledTableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {!loading && transformedData.length > 0 ? (
//                   <>
//                     {sortedData.map((row, index) => (
//                       <StyledTableRow key={index}>
//                         <StyledTableCell
//                           sx={{
//                             textAlign: "left",
//                             pl: isMobile ? 2 : 3,
//                             maxWidth: "200px",
//                             overflow: "hidden",
//                             textOverflow: "ellipsis",
//                             whiteSpace: "nowrap",
//                           }}
//                         >
//                           {row.location}
//                         </StyledTableCell>
//                         <StyledTableCell>{row.TotalEmp}</StyledTableCell>
//                         <StyledTableCell>{row.TodayAtt}</StyledTableCell>
//                         <StyledTableCell>
//                           <Box
//                             sx={{
//                               display: "inline-flex",
//                               alignItems: "center",
//                               gap: 0.5,
//                             }}
//                           >
//                             <span>{Math.round(Number(row.Percentage))}%</span>
//                             <Box
//                               sx={{
//                                 width: "6px",
//                                 height: "6px",
//                                 borderRadius: "50%",
//                                 backgroundColor:
//                                   Number(row.Percentage) > 70
//                                     ? "#4CAF50"
//                                     : Number(row.Percentage) > 50
//                                     ? "#FF9800"
//                                     : "#F44336",
//                               }}
//                             />
//                           </Box>
//                         </StyledTableCell>
//                       </StyledTableRow>
//                     ))}
//                     <StyledTableRow
//                       sx={{
//                         backgroundColor: "#154c79 !important",
//                         "&:hover": { backgroundColor: "#154c79 !important" },
//                       }}
//                     >
//                       <StyledTableCell
//                         sx={{
//                           color: "#ffffff",
//                           fontWeight: 700,
//                           textAlign: "left",
//                           pl: isMobile ? 2 : 3,
//                         }}
//                       >
//                         {t.total}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStrength}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {activeCount}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         <Box
//                           sx={{
//                             display: "inline-flex",
//                             alignItems: "center",
//                             gap: 0.5,
//                           }}
//                         >
//                           <span>{activePercentage}%</span>
//                           <Box
//                             sx={{
//                               width: "6px",
//                               height: "6px",
//                               borderRadius: "50%",
//                               backgroundColor:
//                                 activePercentage > 70
//                                   ? "#4CAF50"
//                                   : activePercentage > 50
//                                   ? "#FF9800"
//                                   : "#F44336",
//                             }}
//                           />
//                         </Box>
//                       </StyledTableCell>
//                     </StyledTableRow>
//                   </>
//                 ) : loading ? (
//                   <TableRow>
//                     <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
//                       <Typography
//                         sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
//                       >
//                         {t.loading}
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 ) : (
//                   <TableRow>
//                     <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
//                       <Typography
//                         sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
//                       >
//                         {t.noData}
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 )}
//               </TableBody>
//             </Table>
//           </StyledTableContainer>
//         </Box>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// }

// import React, { useEffect, useState, useMemo, useRef } from "react";
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   Paper,
//   Table,
//   TableHead,
//   TableBody,
//   TableRow,
//   TableCell,
//   TableContainer,
//   Snackbar,
//   Alert,
//   LinearProgress,
//   CircularProgress,
//   Backdrop,
// } from "@mui/material";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import { styled } from "@mui/material/styles";
// import { useTheme, useMediaQuery } from "@mui/material";
// import { LocalizationProvider } from "@mui/x-date-pickers";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchLocationBased } from "../../actions/LocationBasedAction";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
//   CartesianGrid,
//   Cell,
//   LabelList,
// } from "recharts";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import html2canvas from "html2canvas";

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "1000px",
//   margin: "0 auto",
//   padding: "8px 12px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     padding: "0px 8px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "10px",
//   boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
//   border: "1px solid #d1d9e6",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "5px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "2px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   [theme.breakpoints.up("sm")]: {
//     maxHeight: "380px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   [theme.breakpoints.down("sm")]: {
//     maxHeight: "none",
//     overflowY: "visible",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "8px 6px",
//   fontSize: "13px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.3px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "10px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "10px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: "6px 4px",
//     fontSize: "11px",
//     "&.MuiTableCell-head": {
//       fontSize: "12px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.15s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.06)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "10px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "10px",
//   },
// }));

// const ChartContainer = styled(Box)(({ theme }) => ({
//   position: "relative",
//   backgroundColor: "#f9fafc",
//   borderRadius: "10px",
//   padding: theme.spacing(1.5),
//   boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
//   border: "1px solid #e6e9ef",
//   marginBottom: theme.spacing(1.5),
//   [theme.breakpoints.down("sm")]: {
//     padding: theme.spacing(1),
//   },
// }));

// const ChartTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "14px",
//   fontWeight: 700,
//   color: "#0e3c66",
//   marginBottom: theme.spacing(1),
//   textAlign: "center",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "12px",
//   },
// }));

// const CustomTooltip = ({ active, payload }) => {
//   if (active && payload && payload.length) {
//     const data = payload[0].payload;
//     return (
//       <Paper
//         sx={{
//           padding: "8px 10px",
//           backgroundColor: "rgba(255, 255, 255, 0.98)",
//           border: "1px solid #154c79",
//           borderRadius: "6px",
//           boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
//           minWidth: "150px",
//         }}
//       >
//         <Box sx={{ mb: 0.5 }}>
//           <Typography
//             sx={{
//               fontSize: "12px",
//               fontWeight: 800,
//               color: "#154c79",
//               textTransform: "uppercase",
//             }}
//           >
//             {data.location}
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               Total Strength:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0e3c66" }}>
//               {data.total}
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               Attendance:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32" }}>
//               {data.real} ({data.percentage}%)
//             </Typography>
//           </Box>

//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "10px", color: "#666", fontWeight: 500 }}>
//               Absent:
//             </Typography>
//             <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#F44336" }}>
//               {data.absent}
//             </Typography>
//           </Box>
//         </Box>
//       </Paper>
//     );
//   }
//   return null;
// };

// export default function LocationBasedEmp() {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { loading, data } = useSelector((state) => state.locationBased);

//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [exportProgress, setExportProgress] = useState(0);
//   const [exporting, setExporting] = useState(false);
//   const [snackbar, setSnackbar] = useState({
//     open: false,
//     message: "",
//     severity: "success",
//   });

//   const chartContainerRef = useRef(null);

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchLocationBased(formatDate(new Date())));
//   }, [dispatch]);

//   const handleDateChange = (date) => {
//     setSelectedDate(date);
//     setOpenDatePicker(false);
//     const formatted = formatDate(date);
//     dispatch(fetchLocationBased(formatted));
//   };

//   const showSnackbar = (message, severity = "success") => {
//     setSnackbar({
//       open: true,
//       message,
//       severity,
//     });
//   };

//   const closeSnackbar = () => {
//     setSnackbar({ ...snackbar, open: false });
//   };

//   // Transform API data
//   const transformedData = useMemo(() => {
//     if (!data || !Array.isArray(data)) return [];

//     return data.map((item) => ({
//       Emp_location: item.location || item.Emp_location || "",
//       Emp_Total: item.TotalEmp || item.Emp_Total || "0",
//       Emp_real: item.TodayAtt || item.Emp_real || "0",
//       Percentage: item.Percentage || "0",
//       location: item.location || item.Emp_location || "",
//       TotalEmp: item.TotalEmp || item.Emp_Total || "0",
//       TodayAtt: item.TodayAtt || item.Emp_real || "0",
//     }));
//   }, [data]);

//   const sortedData = transformedData.length > 0
//     ? [...transformedData].sort((a, b) => Number(b.TodayAtt || 0) - Number(a.TodayAtt || 0))
//     : [];

//   // Prepare chart data - Filter out locations with 0% attendance and sort by attendance
//   const chartData = useMemo(() => {
//     if (sortedData.length === 0) return [];

//     return sortedData
//       .filter((item) => Number(item.TodayAtt || 0) > 0)
//       .map((item) => ({
//         location: item.location,
//         total: Number(item.TotalEmp || 0),
//         real: Number(item.TodayAtt || 0),
//         absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//         percentage: Math.round(Number(item.Percentage || 0)),
//       }));
//   }, [sortedData]);

//   const totalStrength =
//     !loading && transformedData.length > 0
//       ? transformedData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0)
//       : 0;

//   const activeCount =
//     !loading && transformedData.length > 0
//       ? transformedData.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0)
//       : 0;

//   const absentCount = totalStrength - activeCount;

//   const activePercentage =
//     totalStrength > 0 ? Math.round((activeCount / totalStrength) * 100) : 0;

//   // ================= COMBINED PDF EXPORT FUNCTION =================
//   const handleExportPDF = async () => {
//     if (!chartContainerRef.current || exporting) return;
    
//     try {
//       setExporting(true);
//       setExportProgress(10);
      
//       // Step 1: Capture chart as image
//       setExportProgress(30);
//       const chartCanvas = await html2canvas(chartContainerRef.current, {
//         scale: 2,
//         backgroundColor: "#F9FAFC",
//         logging: false,
//         useCORS: true,
//       });
//       const chartImage = chartCanvas.toDataURL("image/png");
      
//       setExportProgress(60);
      
//       // Step 2: Create PDF document
//       const doc = new jsPDF("p", "mm", "a4");
//       const pageWidth = doc.internal.pageSize.getWidth();
//       const pageHeight = doc.internal.pageSize.getHeight();
      
//       // Page 1: Cover page with title and summary
//       doc.setFontSize(20);
//       doc.setFont("helvetica", "bold");
//       doc.text("Employee Strength & Attendance Report", pageWidth / 2, 40, { align: "center" });
      
//       doc.setFontSize(14);
//       doc.setFont("helvetica", "normal");
//       doc.text(`Report Date: ${formatDate(selectedDate)}`, pageWidth / 2, 60, { align: "center" });
      
//       // Summary statistics
//       doc.setFontSize(12);
//       doc.text("Summary Statistics", pageWidth / 2, 80, { align: "center" });
      
//       const summaryY = 90;
//       doc.text(`Total Strength: ${totalStrength}`, 30, summaryY);
//       doc.text(`Total Attendance: ${activeCount} (${activePercentage}%)`, 30, summaryY + 8);
//       doc.text(`Total Absent: ${absentCount} (${100 - activePercentage}%)`, 30, summaryY + 16);
      
//       // Add decorative line
//       doc.setDrawColor(21, 76, 121);
//       doc.setLineWidth(0.5);
//       doc.line(30, summaryY + 25, pageWidth - 30, summaryY + 25);
      
//       setExportProgress(70);
      
//       // Page 2: Chart Visualization
//       doc.addPage();
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text("Attendance Visualization by Location", 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`Date: ${formatDate(selectedDate)}`, 20, 28);
//       doc.text(`Chart shows ${chartData.length} locations with active attendance`, 20, 34);
      
//       // Add chart image
//       const chartWidth = pageWidth - 40;
//       const chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;
//       let chartY = 45;
      
//       if (chartHeight > pageHeight - chartY - 20) {
//         const maxHeight = pageHeight - chartY - 20;
//         const scaledWidth = (chartCanvas.width * maxHeight) / chartCanvas.height;
//         const chartX = (pageWidth - scaledWidth) / 2;
//         doc.addImage(chartImage, "PNG", chartX, chartY, scaledWidth, maxHeight);
//       } else {
//         const chartX = (pageWidth - chartWidth) / 2;
//         doc.addImage(chartImage, "PNG", chartX, chartY, chartWidth, chartHeight);
//       }
      
//       setExportProgress(80);
      
//       // Page 3: Detailed Table
//       doc.addPage();
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.text("Detailed Attendance Data", 20, 20);
      
//       doc.setFontSize(12);
//       doc.setFont("helvetica", "normal");
//       doc.text(`Date: ${formatDate(selectedDate)}`, 20, 28);
      
//       // Prepare table data
//       const tableData = sortedData.map((item) => [
//         item.location,
//         item.TotalEmp.toString(),
//         item.TodayAtt.toString(),
//         (Number(item.TotalEmp) - Number(item.TodayAtt)).toString(),
//         `${Math.round(Number(item.Percentage))}%`,
//       ]);
      
//       // Add total row
//       tableData.push([
//         "TOTAL",
//         totalStrength.toString(),
//         activeCount.toString(),
//         absentCount.toString(),
//         `${activePercentage}%`,
//       ]);
      
//       setExportProgress(90);
      
//       // Generate table
//       autoTable(doc, {
//         startY: 35,
//         head: [
//           ["Location", "Total Strength", "Attendance", "Absent", "Attendance Rate (%)"]
//         ],
//         body: tableData,
//         styles: {
//           fontSize: 9,
//           cellPadding: 4,
//         },
//         headStyles: {
//           fillColor: [21, 76, 121],
//           textColor: 255,
//           fontStyle: "bold",
//         },
//         alternateRowStyles: {
//           fillColor: [245, 248, 252],
//         },
//         didDrawCell: (data) => {
//           // Highlight total row
//           if (data.row.index === tableData.length - 1) {
//             doc.setFillColor(21, 76, 121);
//             doc.setTextColor(255, 255, 255);
//             doc.setFont("helvetica", "bold");
//           }
//         },
//       });
      
//       // Footer on all pages
//       const pageCount = doc.internal.getNumberOfPages();
//       for (let i = 1; i <= pageCount; i++) {
//         doc.setPage(i);
//         doc.setFontSize(10);
//         doc.setTextColor(128, 128, 128);
//         doc.text(
//           `Page ${i} of ${pageCount}`,
//           pageWidth - 20,
//           pageHeight - 10,
//           { align: "right" }
//         );
//         doc.text(
//           `Generated on ${new Date().toLocaleDateString()}`,
//           20,
//           pageHeight - 10
//         );
//       }
      
//       setExportProgress(100);
      
//       // Save the PDF
//       doc.save(`Complete_Attendance_Report_${formatDate(selectedDate)}.pdf`);
      
//       // Show success message
//       showSnackbar("PDF exported successfully!", "success");
      
//       // Reset progress after a delay
//       setTimeout(() => {
//         setExporting(false);
//         setExportProgress(0);
//       }, 1000);
      
//     } catch (error) {
//       console.error("Error generating PDF:", error);
//       showSnackbar("Failed to export PDF. Please try again.", "error");
//       setExporting(false);
//       setExportProgress(0);
//     }
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         {/* Export Progress Backdrop */}
//         <Backdrop
//           sx={{
//             color: "#fff",
//             zIndex: (theme) => theme.zIndex.drawer + 1,
//             backgroundColor: "rgba(0, 0, 0, 0.7)",
//           }}
//           open={exporting}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//               gap: 2,
//               p: 4,
//               bgcolor: "background.paper",
//               borderRadius: 2,
//               boxShadow: 24,
//               maxWidth: 400,
//               width: "100%",
//             }}
//           >
//             <CircularProgress
//               size={60}
//               thickness={4}
//               sx={{ color: "#154c79" }}
//             />
//             <Typography variant="h6" color="primary" fontWeight="bold">
//               Exporting PDF...
//             </Typography>
//             <Box sx={{ width: "100%", mt: 2 }}>
//               <LinearProgress
//                 variant="determinate"
//                 value={exportProgress}
//                 sx={{
//                   height: 10,
//                   borderRadius: 5,
//                   backgroundColor: "#e0e0e0",
//                   "& .MuiLinearProgress-bar": {
//                     backgroundColor: "#2e7d32",
//                   },
//                 }}
//               />
//               <Typography
//                 variant="body2"
//                 color="text.secondary"
//                 align="center"
//                 mt={1}
//               >
//                 {exportProgress}% Complete
//               </Typography>
//             </Box>
//             <Typography variant="body2" color="text.secondary" align="center">
//               Preparing your report with chart and table data...
//             </Typography>
//           </Box>
//         </Backdrop>

//         {/* Success/Error Snackbar */}
//         <Snackbar
//           open={snackbar.open}
//           autoHideDuration={4000}
//           onClose={closeSnackbar}
//           anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         >
//           <Alert
//             onClose={closeSnackbar}
//             severity={snackbar.severity}
//             sx={{ width: "100%" }}
//             elevation={6}
//             variant="filled"
//           >
//             {snackbar.message}
//           </Alert>
//         </Snackbar>

//         <Typography
//           sx={{
//             fontSize: "16px",
//             fontWeight: 700,
//             color: "#0e3c66",
//             textAlign: "center",
//             mb: 1,
//           }}
//         >
//           Employee Strength & Attendance
//         </Typography>

//         {/* Date Picker and Export Button - Inline Layout */}
//         <Box
//           sx={{
//             display: "flex",
//             gap: 1,
//             mb: 1.5,
//             alignItems: "center",
//             flexWrap: isMobile ? "wrap" : "nowrap",
//           }}
//         >
//           <Button
//             variant="contained"
//             startIcon={<CalendarTodayIcon />}
//             onClick={() => setOpenDatePicker(true)}
//             disabled={exporting}
//             sx={{
//               flex: isMobile ? 1 : 3,
//               minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//               height: "38px",
//               borderRadius: "8px",
//               backgroundColor: exporting ? "#9e9e9e" : "#142c54",
//               color: "#fff",
//               fontWeight: "bold",
//               textTransform: "none",
//               fontSize: "13px",
//               "&:hover": { 
//                 backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
//               },
//             }}
//           >
//             {formatDate(selectedDate)}
//           </Button>

//           {!loading && transformedData.length > 0 && (
//             <Button
//               variant="contained"
//               startIcon={<PictureAsPdfIcon />}
//               onClick={handleExportPDF}
//               disabled={exporting || chartData.length === 0}
//               sx={{
//                 flex: 1,
//                 minWidth: isMobile ? "calc(50% - 4px)" : "auto",
//                 height: "38px",
//                 borderRadius: "8px",
//                 backgroundColor: exporting 
//                   ? "#9e9e9e" 
//                   : (chartData.length === 0 ? "#bdbdbd" : "#2e7d32"),
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "13px",
//                 "&:hover": { 
//                   backgroundColor: exporting 
//                     ? "#9e9e9e" 
//                     : (chartData.length === 0 ? "#bdbdbd" : "#1b5e20") 
//                 },
//               }}
//             >
//               {exporting ? "Exporting..." : "Export Full PDF"}
//             </Button>
//           )}
//         </Box>

//         {openDatePicker && (
//           <DatePicker
//             open={openDatePicker}
//             value={selectedDate}
//             onChange={handleDateChange}
//             onClose={() => setOpenDatePicker(false)}
//             renderInput={(params) => (
//               <TextField {...params} sx={{ display: "none" }} />
//             )}
//           />
//         )}

//         {/* Sticky Cards Section - All in one row */}
//         {!loading && transformedData.length > 0 && (
//           <StickyCardsContainer>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: "6px",
//               }}
//             >
//               {/* Total Strength Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "1px solid #aec4e0",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   Total Strength
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {totalStrength}
//                 </Typography>
//               </Box>

//               {/* Attendance Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
//                   border: "1px solid #80cbc4",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#004d40",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   Attendance
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {activeCount}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color:
//                       activePercentage > 70
//                         ? "#2E7D32"
//                         : activePercentage > 50
//                         ? "#FF9800"
//                         : "#D32F2F",
//                   }}
//                 >
//                   ({activePercentage}%)
//                 </Typography>
//               </Box>

//               {/* Absent Card */}
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
//                   border: "1px solid #ef9a9a",
//                   padding: "6px 8px",
//                   borderRadius: "6px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#b71c1c",
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}>
//                   Absent
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
//                   {absentCount}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     fontSize: isMobile ? "9px" : "10px",
//                     fontWeight: 700,
//                     color: "#D32F2F",
//                   }}
//                 >
//                   ({100 - activePercentage}%)
//                 </Typography>
//               </Box>
//             </Box>
//           </StickyCardsContainer>
//         )}

//         {/* Content Container */}
//         <Box
//           sx={{
//             [theme.breakpoints.down("sm")]: {
//               maxHeight: "calc(100vh - 260px)",
//               overflowY: "auto",
//             },
//           }}
//         >
//           {/* Chart Section - Added ref for PDF export */}
//           {chartData.length > 0 && (
//             <ChartContainer ref={chartContainerRef}>
//               <ChartTitle>
//                 Attendance by Location ({chartData.length} locations with attendance)
//               </ChartTitle>

//               <Box sx={{ width: "100%", height: isMobile ? 450 : 420 }}>
//                 <ResponsiveContainer width="100%" height="100%">
//                   <BarChart
//                     data={chartData}
//                     layout="vertical"
//                     barSize={isMobile ? 24 : 28}
//                     margin={{
//                       top: 5,
//                       right: isMobile ? 10 : 180,
//                       left: 0,
//                       bottom: 5,
//                     }}
//                   >
//                     <CartesianGrid
//                       strokeDasharray="2 2"
//                       stroke="#e0e0e0"
//                       horizontal={true}
//                       vertical={false}
//                     />
                    
//                     <YAxis
//                       dataKey="location"
//                       type="category"
//                       axisLine={false}
//                       tickLine={false}
//                       tick={{
//                         fontSize: isMobile ? 10 : 11,
//                         fontWeight: 500,
//                         fill: "#333",
//                         textAnchor: "start",
//                         dx: -150,
//                       }}
//                       width={isMobile ? 170 : 180}
//                       interval={0}
//                       tickFormatter={(value) => value}
//                     />

//                     <XAxis
//                       type="number"
//                       tick={{ fontSize: isMobile ? 9 : 10 }}
//                       axisLine={false}
//                       tickLine={false}
//                       domain={[0, "dataMax + 5"]}
//                       tickCount={6}
//                     />

//                     <Tooltip
//                       content={<CustomTooltip />}
//                       cursor={{ fill: "rgba(0,0,0,0.03)" }}
//                     />

//                     <Bar
//                       dataKey="real"
//                       name="Attendance"
//                       fill="#4CAF50"
//                       radius={[0, 10, 10, 0]}
//                     >
//                       {chartData.map((entry, index) => (
//                         <Cell
//                           key={`attendance-cell-${index}`}
//                           fill={
//                             entry.percentage > 70
//                               ? "#4CAF50"
//                               : entry.percentage > 50
//                               ? "#FF9800"
//                               : "#F44336"
//                           }
//                         />
//                       ))}

//                       <LabelList
//                         dataKey="real"
//                         position="insideLeft"
//                         style={{
//                           fontSize: isMobile ? 9 : 10,
//                           fontWeight: 700,
//                           fill: "#fff",
//                         }}
//                         offset={12}
//                       />

//                       <LabelList
//                         dataKey="percentage"
//                         position="right"
//                         formatter={(value) => `${value}%`}
//                         style={{
//                           fontSize: isMobile ? 10 : 11,
//                           fontWeight: 600,
//                           fill: "#555",
//                         }}
//                         offset={8}
//                       />
//                     </Bar>
//                   </BarChart>
//                 </ResponsiveContainer>
//               </Box>

//               {/* Color Legend */}
//               <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   gap: "15px",
//                   mt: 1,
//                   flexWrap: "wrap",
//                 }}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#4CAF50",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     High Attendance ({'>'}70%)
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#FF9800",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     Medium (50-70%)
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
//                   <Box
//                     sx={{
//                       width: "14px",
//                       height: "14px",
//                       backgroundColor: "#F44336",
//                       borderRadius: "3px",
//                     }}
//                   />
//                   <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
//                     Low ({'<'}50%)
//                   </Typography>
//                 </Box>
//               </Box>
//             </ChartContainer>
//           )}

//           {/* TABLE SECTION */}
//           <StyledTableContainer component={Paper}>
//             <Table stickyHeader>
//               <TableHead>
//                 <TableRow>
//                   <StyledTableCell>Location</StyledTableCell>
//                   <StyledTableCell>Strength</StyledTableCell>
//                   <StyledTableCell>Attendance</StyledTableCell>
//                   <StyledTableCell>%</StyledTableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {!loading && transformedData.length > 0 ? (
//                   <>
//                     {sortedData.map((row, index) => (
//                       <StyledTableRow key={index}>
//                         <StyledTableCell
//                           sx={{
//                             textAlign: "left",
//                             pl: isMobile ? 2 : 3,
//                             maxWidth: "200px",
//                             overflow: "hidden",
//                             textOverflow: "ellipsis",
//                             whiteSpace: "nowrap",
//                           }}
//                         >
//                           {row.location}
//                         </StyledTableCell>
//                         <StyledTableCell>{row.TotalEmp}</StyledTableCell>
//                         <StyledTableCell>{row.TodayAtt}</StyledTableCell>
//                         <StyledTableCell>
//                           <Box
//                             sx={{
//                               display: "inline-flex",
//                               alignItems: "center",
//                               gap: 0.5,
//                             }}
//                           >
//                             <span>{Math.round(Number(row.Percentage))}%</span>
//                             <Box
//                               sx={{
//                                 width: "6px",
//                                 height: "6px",
//                                 borderRadius: "50%",
//                                 backgroundColor:
//                                   Number(row.Percentage) > 70
//                                     ? "#4CAF50"
//                                     : Number(row.Percentage) > 50
//                                     ? "#FF9800"
//                                     : "#F44336",
//                               }}
//                             />
//                           </Box>
//                         </StyledTableCell>
//                       </StyledTableRow>
//                     ))}
//                     <StyledTableRow
//                       sx={{
//                         backgroundColor: "#154c79 !important",
//                         "&:hover": { backgroundColor: "#154c79 !important" },
//                       }}
//                     >
//                       <StyledTableCell
//                         sx={{
//                           color: "#ffffff",
//                           fontWeight: 700,
//                           textAlign: "left",
//                           pl: isMobile ? 2 : 3,
//                         }}
//                       >
//                         Total
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {totalStrength}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         {activeCount}
//                       </StyledTableCell>
//                       <StyledTableCell
//                         sx={{ color: "#ffffff", fontWeight: 700 }}
//                       >
//                         <Box
//                           sx={{
//                             display: "inline-flex",
//                             alignItems: "center",
//                             gap: 0.5,
//                           }}
//                         >
//                           <span>{activePercentage}%</span>
//                           <Box
//                             sx={{
//                               width: "6px",
//                               height: "6px",
//                               borderRadius: "50%",
//                               backgroundColor:
//                                 activePercentage > 70
//                                   ? "#4CAF50"
//                                   : activePercentage > 50
//                                   ? "#FF9800"
//                                   : "#F44336",
//                             }}
//                           />
//                         </Box>
//                       </StyledTableCell>
//                     </StyledTableRow>
//                   </>
//                 ) : loading ? (
//                   <TableRow>
//                     <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
//                       <Typography
//                         sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
//                       >
//                         Loading attendance data...
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 ) : (
//                   <TableRow>
//                     <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
//                       <Typography
//                         sx={{ color: "#666", fontStyle: "italic", fontSize: "13px" }}
//                       >
//                         No attendance data available for the selected date.
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 )}
//               </TableBody>
//             </Table>
//           </StyledTableContainer>
//         </Box>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// }








// import React, { useEffect, useState, useMemo } from "react";
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   Paper,
//   Table,
//   TableHead,
//   TableBody,
//   TableRow,
//   TableCell,
//   TableContainer,
//   Grid,
// } from "@mui/material";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import { styled } from "@mui/material/styles";
// import { useTheme, useMediaQuery } from "@mui/material";
// import { LocalizationProvider } from "@mui/x-date-pickers";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import { useDispatch, useSelector } from "react-redux";
// import { fetchLocationBased } from "../../actions/LocationBasedAction";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
//   CartesianGrid,
//   Cell,
//   LabelList,
// } from "recharts";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";

// // ------------ Styles ------------
// const PageContainer = styled(Box)(({ theme }) => ({
//   maxWidth: "900px",
//   margin: "0 auto",
//   padding: "10px 15px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down('sm')]: {
//     padding: "10px 10px",
//   },
// }));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "5px",
//   boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
//   [theme.breakpoints.down('sm')]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

// // REMOVED ScrollableContent style as it's no longer needed

// const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
//   borderRadius: "16px",
//   boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
//   border: "1px solid #d6dce5",
//   backgroundColor: "#ffffff",
//   overflow: "hidden",
//   "& .MuiTable-root": {
//     borderCollapse: "separate",
//     borderSpacing: 0,
//   },
//   "&::-webkit-scrollbar": {
//     width: "8px",
//   },
//   "&::-webkit-scrollbar-thumb": {
//     backgroundColor: "#888",
//     borderRadius: "4px",
//   },
//   "&::-webkit-scrollbar-thumb:hover": {
//     backgroundColor: "#555",
//   },
//   // Desktop view: Fixed height with scroll
//   [theme.breakpoints.up('sm')]: {
//     maxHeight: "450px",
//     overflowY: "auto",
//     overflowX: "auto",
//   },
//   // Mobile view: Auto height, parent container handles scrolling
//   [theme.breakpoints.down('sm')]: {
//     maxHeight: "none",
//     overflowY: "visible",
//     overflowX: "auto",
//   },
// }));

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   fontFamily: "'Poppins', sans-serif",
//   padding: "14px 10px",
//   fontSize: "14px",
//   textAlign: "center",
//   "&.MuiTableCell-head": {
//     backgroundColor: "#154c79",
//     color: "#ffffff",
//     fontWeight: 600,
//     letterSpacing: "0.5px",
//     borderBottom: "none",
//     position: "sticky",
//     top: 0,
//     zIndex: 10,
//     "&:first-of-type": {
//       borderTopLeftRadius: "16px",
//     },
//     "&:last-of-type": {
//       borderTopRightRadius: "16px",
//     },
//   },
//   [theme.breakpoints.down('sm')]: {
//     padding: "12px 8px",
//     fontSize: "12px",
//     "&.MuiTableCell-head": {
//       fontSize: "13px",
//     },
//   },
// }));

// const StyledTableRow = styled(TableRow)(() => ({
//   transition: "0.25s ease",
//   "&:nth-of-type(odd)": {
//     backgroundColor: "rgba(243, 246, 250, 0.6)",
//   },
//   "&:hover": {
//     backgroundColor: "rgba(14, 60, 102, 0.09)",
//     cursor: "pointer",
//   },
//   "&:last-of-type td:first-of-type": {
//     borderBottomLeftRadius: "16px",
//   },
//   "&:last-of-type td:last-of-type": {
//     borderBottomRightRadius: "16px",
//   },
// }));

// const ChartContainer = styled(Box)(({ theme }) => ({
//   position: "relative",
//   backgroundColor: "#f9fafc",
//   borderRadius: "16px",
//   padding: theme.spacing(3),
//   boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
//   border: "1px solid #e6e9ef",
//   marginBottom: theme.spacing(3),
//   [theme.breakpoints.down('sm')]: {
//     padding: theme.spacing(2),
//   },
// }));

// const ChartTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "16px",
//   fontWeight: 700,
//   color: "#0e3c66",
//   marginBottom: theme.spacing(2),
//   textAlign: "center",
//   [theme.breakpoints.down('sm')]: {
//     fontSize: "14px",
//     marginBottom: theme.spacing(1),
//   },
// }));

// const CustomLegend = ({ isMobile }) => {
//   return (
//     <Box
//       sx={{
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//         gap: isMobile ? "15px" : "25px",
//         mt: 2,
//         mb: 2,
//       }}
//     >
//       <Box
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           gap: "8px",
//         }}
//       >
//         <Box
//           sx={{
//             width: isMobile ? "16px" : "20px",
//             height: isMobile ? "16px" : "20px",
//             backgroundColor: "#2E7D32",
//             borderRadius: "4px",
//             border: "1px solid rgba(0,0,0,0.1)",
//           }}
//         />
//         <Typography
//           sx={{
//             fontSize: isMobile ? "12px" : "14px",
//             fontWeight: 600,
//             color: "#333",
//           }}
//         >
//           Present
//         </Typography>
//       </Box>
//       <Box
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           gap: "8px",
//         }}
//       >
//         <Box
//           sx={{
//             width: isMobile ? "16px" : "20px",
//             height: isMobile ? "16px" : "20px",
//             backgroundColor: "#FFC107",
//             borderRadius: "4px",
//             border: "1px solid rgba(0,0,0,0.1)",
//           }}
//         />
//         <Typography
//           sx={{
//             fontSize: isMobile ? "12px" : "14px",
//             fontWeight: 600,
//             color: "#333",
//           }}
//         >
//           Absent
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// const CustomTooltip = ({ active, payload }) => {
//   if (active && payload && payload.length) {
//     const data = payload[0].payload;
//     return (
//       <Paper
//         sx={{
//           padding: "12px 16px",
//           backgroundColor: "rgba(255, 255, 255, 0.98)",
//           border: "2px solid #154c79",
//           borderRadius: "10px",
//           boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
//           minWidth: "180px",
//         }}
//       >
//         <Box sx={{ mb: 1 }}>
//           <Typography sx={{ 
//             fontSize: "14px", 
//             fontWeight: 800, 
//             color: "#154c79",
//             textTransform: "uppercase",
//             letterSpacing: "0.5px"
//           }}>
//             {data.location}
//           </Typography>
//         </Box>
        
//         <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Typography sx={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>
//               Total Strength:
//             </Typography>
//             <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#0e3c66" }}>
//               {data.total}
//             </Typography>
//           </Box>
          
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//               <Box sx={{ width: "10px", height: "10px", bgcolor: "#2E7D32", borderRadius: "2px" }} />
//               <Typography sx={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>
//                 Present:
//               </Typography>
//             </Box>
//             <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#2E7D32" }}>
//               {data.real}
//             </Typography>
//           </Box>
          
//           <Box sx={{ display: "flex", justifyContent: "space-between" }}>
//             <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//               <Box sx={{ width: "10px", height: "10px", bgcolor: "#FFC107", borderRadius: "2px" }} />
//               <Typography sx={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>
//                 Absent:
//               </Typography>
//             </Box>
//             <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#F57C00" }}>
//               {data.absent}
//             </Typography>
//           </Box>
          
//           <Box sx={{ 
//             display: "flex", 
//             justifyContent: "space-between", 
//             pt: 1, 
//             borderTop: "1px dashed #ddd",
//             alignItems: "center"
//           }}>
//             <Typography sx={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>
//               Attendance Rate:
//             </Typography>
//             <Box sx={{ 
//               px: 1.5, 
//               py: 0.5, 
//               borderRadius: "12px",
//               backgroundColor: data.percentage > 70 ? "rgba(46, 125, 50, 0.1)" : 
//                               data.percentage > 50 ? "rgba(255, 152, 0, 0.1)" : 
//                               "rgba(211, 47, 47, 0.1)",
//             }}>
//               <Typography sx={{ 
//                 fontSize: "13px", 
//                 fontWeight: 800, 
//                 color: data.percentage > 70 ? "#2E7D32" : 
//                        data.percentage > 50 ? "#FF9800" : 
//                        "#D32F2F"
//               }}>
//                 {data.percentage}%
//               </Typography>
//             </Box>
//           </Box>
//         </Box>
//       </Paper>
//     );
//   }
//   return null;
// };

// export default function LocationBasedEmp() {
//   const dispatch = useDispatch();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
//   const { loading, data } = useSelector((state) => state.locationBased);

//   const [openDatePicker, setOpenDatePicker] = useState(false);
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [currentPage, setCurrentPage] = useState(0);
//   const itemsPerPage = isMobile ? 5 : 8;

//   const formatDate = (dateObj) => {
//     const d = new Date(dateObj);
//     return d.toISOString().split("T")[0];
//   };

//   useEffect(() => {
//     dispatch(fetchLocationBased(formatDate(new Date())));
//   }, [dispatch]);

//   useEffect(() => {
//     // Reset to first page when data changes
//     setCurrentPage(0);
//   }, [data]);

//   const handleDateChange = (date) => {
//     setSelectedDate(date);
//     setOpenDatePicker(false);
//     setCurrentPage(0); // Reset to first page on date change
//     const formatted = formatDate(date);
//     dispatch(fetchLocationBased(formatted));
//   };

//   // Transform API data to match component expectations
//   const transformedData = useMemo(() => {
//     if (!data || !Array.isArray(data)) return [];
    
//     return data.map(item => ({
//       Emp_location: item.location || item.Emp_location || "",
//       Emp_Total: item.TotalEmp || item.Emp_Total || "0",
//       Emp_real: item.TodayAtt || item.Emp_real || "0",
//       Percentage: item.Percentage || "0",
//       location: item.location || item.Emp_location || "",
//       TotalEmp: item.TotalEmp || item.Emp_Total || "0",
//       TodayAtt: item.TodayAtt || item.Emp_real || "0",
//     }));
//   }, [data]);

//   const sortedData = transformedData.length > 0 
//     ? [...transformedData].sort((a, b) => a.location.localeCompare(b.location)) 
//     : [];

//   // Prepare chart data - NOW SHOWING ALL DATA
//   const chartData = useMemo(() => {
//     if (sortedData.length === 0) return [];
    
//     // Sort by total strength for better visualization
//     return [...sortedData]
//       .sort((a, b) => Number(b.TotalEmp || 0) - Number(a.TotalEmp || 0))
//       .map(item => ({
//         location: item.location,
//         total: Number(item.TotalEmp || 0),
//         real: Number(item.TodayAtt || 0),
//         absent: Number(item.TotalEmp || 0) - Number(item.TodayAtt || 0),
//         percentage: Math.round(Number(item.Percentage || 0)),
//       }));
//   }, [sortedData]);

//   // Paginate chart data for display
//   const paginatedChartData = chartData.slice(
//     currentPage * itemsPerPage,
//     (currentPage + 1) * itemsPerPage
//   );

//   const totalStrength = !loading && transformedData.length > 0 
//     ? transformedData.reduce((sum, item) => sum + Number(item.TotalEmp || 0), 0)
//     : 0;
  
//   const activeCount = !loading && transformedData.length > 0 
//     ? transformedData.reduce((sum, item) => sum + Number(item.TodayAtt || 0), 0)
//     : 0;
  
//   const activePercentage = totalStrength > 0 
//     ? Math.round((activeCount / totalStrength) * 100)
//     : 0;

//   // ================= PDF EXPORT FUNCTION =================
//   const handleExportPDF = () => {
//     // Change from "l" (landscape) to "p" (portrait)
//     const doc = new jsPDF("p", "mm", "a4");

//     // Title and metadata - adjusted for portrait
//     doc.setFontSize(18);
//     doc.text("Employee Strength & Attendance Report", 105, 15, { align: "center" });

//     doc.setFontSize(11);
//     doc.text(`Date: ${formatDate(selectedDate)}`, 14, 25);
//     doc.text(`Total Strength: ${totalStrength}`, 14, 32);
//     doc.text(`Total Attendance: ${activeCount} (${activePercentage}%)`, 14, 39);

//     // Create the summary table - adjusted startY for portrait
//     autoTable(doc, {
//       startY: 48,
//       head: [[
//         "Location",
//         "Total Strength",
//         "Attendance",
//         "Absent",
//         "Attendance Rate (%)"
//       ]],
//       body: sortedData.map((item) => [
//         item.location,
//         item.TotalEmp.toString(),
//         item.TodayAtt.toString(),
//         (Number(item.TotalEmp) - Number(item.TodayAtt)).toString(),
//         `${Math.round(Number(item.Percentage))}%`
//       ]),
//       styles: {
//         fontSize: 9,
//         cellPadding: 4,
//       },
//       headStyles: {
//         fillColor: [21, 76, 121],
//         textColor: 255,
//         fontStyle: 'bold',
//       },
//       alternateRowStyles: {
//         fillColor: [245, 248, 252],
//       },
//       foot: [
//         [
//           "TOTAL",
//           totalStrength.toString(),
//           activeCount.toString(),
//           (totalStrength - activeCount).toString(),
//           `${activePercentage}%`
//         ]
//       ],
//       footStyles: {
//         fillColor: [21, 76, 121],
//         textColor: 255,
//         fontStyle: 'bold',
//       },
//       didDrawPage: function (data) {
//         // Add page number
//         const pageCount = doc.internal.getNumberOfPages();
//         doc.setFontSize(10);
//         doc.text(
//           `Page ${data.pageNumber} of ${pageCount}`,
//           doc.internal.pageSize.width - 30,
//           doc.internal.pageSize.height - 10
//         );
        
//         // Add report title on each page
//         doc.setFontSize(12);
//         doc.text(
//           "Employee Strength & Attendance Report",
//           doc.internal.pageSize.width / 2,
//           10,
//           { align: "center" }
//         );
//       },
//     });

//     // Save the PDF
//     doc.save(`Employee_Strength_Attendance_${formatDate(selectedDate)}.pdf`);
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <PageContainer>
//         <Typography
//           sx={{
//             fontSize: "20px",
//             fontWeight: 700,
//             color: "#0e3c66",
//             textAlign: "center",
//             mb: 2,
//           }}
//         >
//           Employee Strength & Attendance
//         </Typography>

//         {/* Date Picker and Export Button Row */}
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={12} md={8}>
//             <Button
//               variant="contained"
//               fullWidth
//               startIcon={<CalendarTodayIcon />}
//               onClick={() => setOpenDatePicker(true)}
//               sx={{
//                 height: "56px",
//                 borderRadius: "20px",
//                 backgroundColor: "#142c54",
//                 color: "#fff",
//                 fontWeight: "bold",
//                 textTransform: "none",
//                 fontSize: "16px",
//                 "&:hover": { backgroundColor: "#0D47A1" },
//               }}
//             >
//               {formatDate(selectedDate)}
//             </Button>
//           </Grid>
          
//           <Grid item xs={12} md={4}>
//             {!loading && transformedData.length > 0 && (
//               <Button
//                 variant="contained"
//                 fullWidth
//                 startIcon={<PictureAsPdfIcon />}
//                 onClick={handleExportPDF}
//                 sx={{
//                   height: "56px",
//                   borderRadius: "20px",
//                   backgroundColor: "#2e7d32",
//                   color: "#fff",
//                   fontWeight: "bold",
//                   textTransform: "none",
//                   fontSize: "16px",
//                   "&:hover": { backgroundColor: "#1b5e20" },
//                 }}
//               >
//                 Export PDF
//               </Button>
//             )}
//           </Grid>
//         </Grid>

//         {openDatePicker && (
//           <DatePicker
//             open={openDatePicker}
//             value={selectedDate}
//             onChange={handleDateChange}
//             onClose={() => setOpenDatePicker(false)}
//             renderInput={(params) => (
//               <TextField {...params} sx={{ display: "none" }} />
//             )}
//           />
//         )}

//         {/* Sticky Cards Section */}
//         {!loading && transformedData.length > 0 && (
//           <StickyCardsContainer>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "center",
//                 gap: isMobile ? "10px" : "15px",
//                 px: isMobile ? 1 : 0,
//               }}
//             >
//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "2px solid #bbd0ea",
//                   padding: isMobile ? "10px 12px" : "12px 16px",
//                   borderRadius: "12px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   maxWidth: isMobile ? "48%" : "200px",
//                   boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 500, mb: 0.5 }}>
//                   Total Strength
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "22px" : "26px", fontWeight: 800 }}>
//                   {totalStrength}
//                 </Typography>
//               </Box>

//               <Box
//                 sx={{
//                   background: "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//                   border: "2px solid #bbd0ea",
//                   padding: isMobile ? "10px 12px" : "12px 16px",
//                   borderRadius: "12px",
//                   textAlign: "center",
//                   fontWeight: 600,
//                   color: "#0E3C66",
//                   flex: 1,
//                   maxWidth: isMobile ? "48%" : "200px",
//                   boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
//                 }}
//               >
//                 <Typography sx={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 500, mb: 0.5 }}>
//                   Attendance
//                 </Typography>
//                 <Typography sx={{ fontSize: isMobile ? "22px" : "26px", fontWeight: 800 }}>
//                   {activeCount}
//                 </Typography>
//                 <Typography sx={{ 
//                   fontSize: isMobile ? "11px" : "12px", 
//                   fontWeight: 700, 
//                   color: activePercentage > 70 ? "#2E7D32" : 
//                          activePercentage > 50 ? "#FF9800" : 
//                          "#D32F2F"
//                 }}>
//                   ({activePercentage}%)
//                 </Typography>
//               </Box>
//             </Box>
//           </StickyCardsContainer>
//         )}

//         {/* Content Container with different behavior for mobile/desktop */}
//         <Box sx={{ 
//           // For mobile: scroll entire content
//           [theme.breakpoints.down('sm')]: {
//             maxHeight: "calc(100vh - 280px)",
//             overflowY: "auto",
//           },
//           // For desktop: no scrolling at container level
//           [theme.breakpoints.up('sm')]: {
//             overflowY: "visible",
//           }
//         }}>
//           <CustomLegend isMobile={isMobile} />

//           {/* Chart Section */}
//           <ChartContainer>
//             <ChartTitle>
//               Location Employee Attendance List
//             </ChartTitle>
            
//             <Box sx={{ width: "100%", height: isMobile ? 500 : 450 }}>
//               <ResponsiveContainer width="100%" height="100%">
//                 <BarChart
//                   data={paginatedChartData}
//                   layout="vertical"
//                   barSize={isMobile ? 28 : 32}
//                   margin={{
//                     top: 10,
//                     right: isMobile ? 120 : 140,
//                     left: isMobile ? -5 : 10,
//                     bottom: 10,
//                   }}
//                 >
//                   <CartesianGrid 
//                     strokeDasharray="3 3" 
//                     stroke="#e0e0e0" 
//                     horizontal={true}
//                     vertical={false}
//                   />
                  
//                   <YAxis
//                     dataKey="location"
//                     type="category"
//                     tick={{
//                       fontSize: isMobile ? 11 : 12,
//                       fontWeight: 600,
//                       fill: "#333",
//                     }}
//                     width={isMobile ? 90 : 120}
//                     interval={0}
//                     axisLine={false}
//                     tickLine={false}
//                   />
                  
//                   <XAxis
//                     type="number"
//                     tick={{ fontSize: isMobile ? 10 : 11 }}
//                     axisLine={false}
//                     tickLine={false}
//                     domain={[0, 'dataMax + 5']}
//                   />
                  
//                   <Tooltip 
//                     content={<CustomTooltip />}
//                     cursor={{ fill: 'rgba(0,0,0,0.05)' }}
//                   />
                  
//                   <Bar
//                     dataKey="real"
//                     name="Present"
//                     stackId="a"
//                     fill="#2E7D32"
//                     radius={[0, 0, 0, 0]}
//                   >
//                     {paginatedChartData.map((entry, index) => (
//                       <Cell 
//                         key={`present-cell-${index}`} 
//                         fill="#2E7D32"
//                       />
//                     ))}
//                   </Bar>
                  
//                   <Bar
//                     dataKey="absent"
//                     name="Absent"
//                     stackId="a"
//                     fill="#FFC107"
//                     radius={[0, 4, 4, 0]}
//                   >
//                     {paginatedChartData.map((entry, index) => (
//                       <Cell 
//                         key={`absent-cell-${index}`} 
//                         fill="#FFC107"
//                       />
//                     ))}
                    
//                     <LabelList
//                       dataKey="percentage"
//                       position="right"
//                       formatter={(value) => `${value}%`}
//                       style={{
//                         fontSize: isMobile ? 10 : 11,
//                         fontWeight: 600,
//                         fill: "#555",
//                       }}
//                       offset={10}
//                     />
//                   </Bar>
//                 </BarChart>
//               </ResponsiveContainer>
//             </Box>

//             {/* Pagination Controls - Shows navigation for ALL data */}
//             {chartData.length > itemsPerPage && (
//               <Box sx={{ 
//                 display: "flex", 
//                 justifyContent: "center", 
//                 alignItems: "center", 
//                 gap: 2, 
//                 mt: 3 
//               }}>
//                 <Button
//                   variant="outlined"
//                   size="small"
//                   disabled={currentPage === 0}
//                   onClick={() => setCurrentPage(prev => prev - 1)}
//                   sx={{
//                     minWidth: "100px",
//                     borderRadius: "20px",
//                     textTransform: "none",
//                     fontWeight: 600,
//                     borderColor: "#154c79",
//                     color: "#154c79",
//                     "&:hover": {
//                       borderColor: "#0D47A1",
//                       backgroundColor: "rgba(21, 76, 121, 0.04)",
//                     },
//                   }}
//                 >
//                   Previous
//                 </Button>
                
//                 <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#555" }}>
//                   Page {currentPage + 1} of {Math.ceil(chartData.length / itemsPerPage)}
//                 </Typography>
                
//                 <Button
//                   variant="outlined"
//                   size="small"
//                   disabled={(currentPage + 1) * itemsPerPage >= chartData.length}
//                   onClick={() => setCurrentPage(prev => prev + 1)}
//                   sx={{
//                     minWidth: "100px",
//                     borderRadius: "20px",
//                     textTransform: "none",
//                     fontWeight: 600,
//                     borderColor: "#154c79",
//                     color: "#154c79",
//                     "&:hover": {
//                       borderColor: "#0D47A1",
//                       backgroundColor: "rgba(21, 76, 121, 0.04)",
//                     },
//                   }}
//                 >
//                   Next
//                 </Button>
//               </Box>
//             )}
//           </ChartContainer>

//           {/* TABLE SECTION - Now the table scrolls independently on desktop */}
//           <StyledTableContainer component={Paper}>
//             <Table stickyHeader>
//               <TableHead>
//                 <TableRow>
//                   <StyledTableCell>Location</StyledTableCell>
//                   <StyledTableCell>Strength</StyledTableCell>
//                   <StyledTableCell>Attendance</StyledTableCell>
//                   <StyledTableCell>Percentage</StyledTableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {!loading && transformedData.length > 0 ? (
//                   <>
//                     {sortedData.map((row, index) => (
//                       <StyledTableRow key={index}>
//                         <StyledTableCell>{row.location}</StyledTableCell>
//                         <StyledTableCell>{row.TotalEmp}</StyledTableCell>
//                         <StyledTableCell>{row.TodayAtt}</StyledTableCell>
//                         <StyledTableCell>
//                           <Box sx={{ 
//                             display: "inline-flex", 
//                             alignItems: "center", 
//                             gap: 1 
//                           }}>
//                             <span>{Math.round(Number(row.Percentage))}%</span>
//                             <Box sx={{ 
//                               width: "8px", 
//                               height: "8px", 
//                               borderRadius: "50%",
//                               backgroundColor: 
//                                 Number(row.Percentage) > 70 ? "#2E7D32" :
//                                 Number(row.Percentage) > 50 ? "#FF9800" :
//                                 "#D32F2F"
//                             }} />
//                           </Box>
//                         </StyledTableCell>
//                       </StyledTableRow>
//                     ))}
//                     <StyledTableRow sx={{ 
//                       backgroundColor: "#154c79 !important", 
//                       "&:hover": { backgroundColor: "#154c79 !important" } 
//                     }}>
//                       <StyledTableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
//                         Total
//                       </StyledTableCell>
//                       <StyledTableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
//                         {totalStrength}
//                       </StyledTableCell>
//                       <StyledTableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
//                         {activeCount}
//                       </StyledTableCell>
//                       <StyledTableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
//                         <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
//                           <span>{activePercentage}%</span>
//                           <Box sx={{ 
//                             width: "8px", 
//                             height: "8px", 
//                             borderRadius: "50%",
//                             backgroundColor: 
//                               activePercentage > 70 ? "#4CAF50" :
//                               activePercentage > 50 ? "#FFC107" :
//                               "#F44336"
//                           }} />
//                         </Box>
//                       </StyledTableCell>
//                     </StyledTableRow>
//                   </>
//                 ) : loading ? (
//                   <TableRow>
//                     <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
//                       <Typography sx={{ color: "#666", fontStyle: "italic" }}>
//                         Loading attendance data...
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 ) : (
//                   <TableRow>
//                     <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
//                       <Typography sx={{ color: "#666", fontStyle: "italic" }}>
//                         No attendance data available for the selected date.
//                       </Typography>
//                     </TableCell>
//                   </TableRow>
//                 )}
//               </TableBody>
//             </Table>
//           </StyledTableContainer>
//         </Box>
//       </PageContainer>
//     </LocalizationProvider>
//   );
// }

